import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static planStatesParam = "people_plan_states"
  static selectedPlansParam = "people_plan_ids"
  static planModeParam = "people_plan_mode"
  static showPastParam = "people_show_past"
  static slotTimelinesParam = "people_slot_timeline_keys"
  static viewModeParam = "people_view_mode"
  static timelineSortParam = "people_timeline_sort"
  static timelineSortDirectionParam = "people_timeline_sort_direction"
  static timelineShowAllPeopleParam = "people_timeline_show_all_people"
  static timelinePlanningFilterParam = "people_timeline_planning_filter"
  static peopleSearchParam = "people_search"
  static peoplePlanningFiltersParam = "people_planning_filters"
  static peoplePlanningEffectsParam = "people_planning_effects"
  static excludeArchivedPlansParam = "people_exclude_archived_plans"
  static groupingCompactionStrategyParam = "people_grouping_compaction_strategy"
  static includeUntimedSlotsParam = "people_include_untimed_slots"
  static publicIdentityParam = "identity_key"
  static publicViewModeParam = "filter_mode"
  static legacyPublicOpenShiftsParam = "show_open_shifts"
  static publicCalendarSuggestionDelayMs = 5000
  static publicCalendarSuggestionScrollThreshold = 120
  static publicCalendarSuggestionMuteMs = 48 * 60 * 60 * 1000
  static defaultPublicTextScale = "4"
  static publicInteractionSettlingDuration = 1500
  static values = {
    translations: Object,
    persistUrl: {type: Boolean, default: true},
    publicIdentityEnabled: {type: Boolean, default: false},
    publicSelfSignupEnabled: {type: Boolean, default: false},
    publicFocusJobId: String,
    publicIdentityStorageKey: String,
    publicViewModeStorageKey: String,
    publicTextScaleStorageKey: String,
    publicIdentityCookieName: String,
    publicViewModeCookieName: String,
    publicIdentityStateUrl: String,
    copyLabel: String,
    copiedLabel: String,
    currentView: String,
    currentViewMode: {type: String, default: "list"},
    excludeArchivedPlans: {type: Boolean, default: true},
    timelineShowAllPeople: {type: Boolean, default: false},
    timelinePlanningFilter: String,
    peopleSearch: String,
    peoplePlanningFilters: Object,
    peoplePlanningEffects: Array,
    planToggleConfigs: Object,
  }
  static targets = [
    "allPlansToggle",
    "publicJoinPolicyGroup",
    "publicJoinPolicySection",
    "planToggle",
    "excludeArchivedPlansToggle",
    "pastToggle",
    "slotTimelineToggle",
    "untimedSlotsToggle",
    "allowPublicSelfSignupToggle",
    "section",
    "card",
    "emptyState",
    "publicIdentitySelect",
    "publicIdentitySummary",
    "publicFilterControls",
    "publicViewModeGroup",
    "publicViewModeButton",
    "publicCalendarActions",
    "publicCalendarLauncher",
    "publicCalendarSuggestion",
    "publicTextScaleGroup",
    "publicTextScaleButton",
    "publicTextScaleDot",
    "publicOverlapCollapseTemplate",
    "stickyHeader",
    "configInput",
    "planJoinPolicy",
    "timelineSort",
    "timelineSortDirection",
    "timelineShowAllPeople",
    "timelinePlanningFilter",
    "groupingCompactionStrategy",
    "viewModeLink",
  ]

  connect() {
    const serverIdentityKey = this.selectedPublicIdentityKey()
    const serverViewMode = this.selectedPublicViewMode(serverIdentityKey)
    this.boundUpdateStickyOffset = this.updateStickyOffset.bind(this)
    this.boundHandleTurboRender = this.handleTurboRender.bind(this)
    this.boundHandlePublicCalendarSuggestionScroll = this.handlePublicCalendarSuggestionScroll.bind(this)
    this.publicCalendarSuggestionTimer = null
    this.publicCalendarSuggestionIdentityKey = null
    this.publicCalendarSuggestionStartedAt = Date.now()
    this.publicCalendarSuggestionScrolled = false
    this.publicCalendarSuggestionVisible = false
    this.boundHandlePublicParticipationUpdate = this.handlePublicParticipationUpdate.bind(this)
    this.boundCancelPublicFilterChange = this.cancelPublicFilterChange.bind(this)
    this.publicTextScaleFeedbackTimeout = null
    this.publicFilterChangeFrame = null
    this.publicFilterChangePending = false
    this.publicFilterBusyControl = null
    this.queuedPublicIdentityTrigger = null
    this.publicIdentityStateKey = serverIdentityKey
    this.publicSubmissionSafetyTimeout = null
    this.publicSubmissionPerformanceAction = null
    this.publicParticipationRefresh = null
    this.publicParticipationDisabledButtons = null
    this.publicIdentityStateAbortController = null
    this.peopleSearchReloadTimeout = null
    this.peopleFilterReloadStarted = false
    this.currentPublicViewMode = null
    this.suppressNextPublicIdentityChange = false
    this.publicFocusJobRestored = false
    this.primePublicDomIndex()
    this.loadPublicIdentity()
    this.loadPublicViewMode()
    this.loadPublicTextScale()
    this.lastSelectedPublicIdentityKey = this.selectedPublicIdentityKey()
    this.syncAllPlansControls()
    this.setupStickyHeaderObserver()
    document.addEventListener("turbo:render", this.boundHandleTurboRender)
    if (this.publicIdentityEnabledValue && this.hasPublicCalendarSuggestionTarget) {
      this.publicCalendarSuggestionScrolled = this.publicCalendarSuggestionScrollSatisfied()
      window.addEventListener("scroll", this.boundHandlePublicCalendarSuggestionScroll, {passive: true})
    }
    document.addEventListener("public-participation:updated", this.boundHandlePublicParticipationUpdate)
    document.addEventListener("turbo:before-cache", this.boundCancelPublicFilterChange)
    this.renderPublicFilterBusy(false)
    this.renderPublicCalendarLinks(this.selectedPublicIdentityKey())
    this.schedulePublicCalendarSuggestion(this.selectedPublicIdentityKey())
    this.updateViewModeLinks(new URL(window.location.href))
    const restoredPublicState = this.publicIdentityEnabledValue && (
      this.element.dataset.publicIdentityStatePending === "true" ||
      serverIdentityKey !== this.selectedPublicIdentityKey() ||
      serverViewMode !== this.selectedPublicViewMode()
    )

    if (!this.publicIdentityEnabledValue) {
      this.withPublicSlotsDetached(() => this.apply(null, {persistUrl: false}))
    } else if (restoredPublicState) {
      this.applyPublicFilterChange({identityChanged: true, trigger: this.publicIdentitySelectTarget})
    } else {
      this.restorePublicFocusJob()
    }
  }

  disconnect() {
    document.removeEventListener("turbo:render", this.boundHandleTurboRender)
    window.removeEventListener("scroll", this.boundHandlePublicCalendarSuggestionScroll)
    document.removeEventListener("public-participation:updated", this.boundHandlePublicParticipationUpdate)
    document.removeEventListener("turbo:before-cache", this.boundCancelPublicFilterChange)
    this.teardownStickyHeaderObserver()
    this.clearPublicTextScaleFeedback()
    this.clearPublicCalendarSuggestionTimer()
    this.cancelPublicFilterChange()
    window.clearTimeout(this.publicSubmissionSafetyTimeout)
    this.publicSubmissionSafetyTimeout = null
    this.publicIdentityStateAbortController?.abort()
    this.publicIdentityStateAbortController = null
    window.clearTimeout(this.peopleSearchReloadTimeout)
    this.peopleSearchReloadTimeout = null
    this.resetPublicDomIndex()
  }

  changePublicIdentity(event) {
    if (this.suppressNextPublicIdentityChange) {
      this.suppressNextPublicIdentityChange = false
      this.lastSelectedPublicIdentityKey = this.selectedPublicIdentityKey()
      this.savePublicIdentity()
      this.savePublicIdentityCookie()
      return
    }

    const selectedIdentityKey = this.selectedPublicIdentityKey()
    if (
      selectedIdentityKey === this.lastSelectedPublicIdentityKey &&
      selectedIdentityKey === this.publicIdentityStateKey
    ) {
      return
    }

    if (this.publicFilterChangePending) {
      this.queuedPublicIdentityTrigger = this.publicFilterTriggerFor(event)
      return
    }

    if (selectedIdentityKey && !this.lastSelectedPublicIdentityKey && this.selectedPublicViewMode("") === "all") {
      this.setPublicViewMode("mine_and_open")
    } else if (!selectedIdentityKey && !this.publicViewModeAvailableWithoutIdentity(this.selectedPublicViewMode())) {
      this.setPublicViewMode("all")
    }

    this.lastSelectedPublicIdentityKey = selectedIdentityKey
    this.savePublicIdentity()
    this.savePublicIdentityCookie()
    this.savePublicViewMode()
    this.savePublicViewModeCookie()
    this.persistPublicSelectionToUrl()
    this.applyPublicFilterChange({identityChanged: true, trigger: this.publicFilterTriggerFor(event)})
  }

  selectPublicViewMode(event) {
    if (this.publicFilterChangePending) {
      this.renderPublicViewModeControls(this.selectedPublicViewMode())
      return
    }

    this.setPublicViewMode(event.currentTarget.dataset.publicViewMode || event.currentTarget.value)

    this.savePublicViewMode()
    this.savePublicViewModeCookie()
    this.persistPublicSelectionToUrl()
    this.applyPublicFilterChange({identityChanged: false, trigger: event.currentTarget})
  }

  adjustPublicTextScale(event) {
    const direction = Number.parseInt(event.currentTarget.dataset.publicTextScaleDirection || "0", 10)
    if (!Number.isFinite(direction) || direction === 0) return

    const nextScale = String(Math.max(1, Math.min(5, Number.parseInt(this.selectedPublicTextScale(), 10) + direction)))
    if (nextScale === this.selectedPublicTextScale()) return

    this.setPublicTextScale(nextScale, {announceChange: true})
    this.savePublicTextScale()
    this.updateStickyOffset()
  }

  applyPublicFilterChange({identityChanged = true, trigger = null} = {}) {
    if (this.publicFilterChangePending) return

    if (identityChanged) {
      this.setPublicParticipationRefresh(null)
      this.publicIdentityStateAbortController?.abort()
      this.publicIdentityStateAbortController = null
    }
    this.publicFilterChangePending = true
    this.renderPublicFilterBusy(true, trigger)
    const performanceAction = this.beginPublicPerformanceAction(
      identityChanged ? "select_identity" : `switch_filter_${this.selectedPublicViewMode()}`
    )

    this.publicFilterChangeFrame = window.requestAnimationFrame(() => {
      this.publicFilterChangeFrame = null
      this.recordPublicPerformancePhase(performanceAction, "reaction")

      if (identityChanged) {
        this.loadPublicIdentityState()
          .then(() => this.completePublicFilterChange({identityChanged: true, performanceAction}))
          .catch((error) => this.handlePublicIdentityStateError(error))
        return
      }

      this.publicFilterChangeFrame = window.requestAnimationFrame(() => {
        this.publicFilterChangeFrame = null
        this.completePublicFilterChange({identityChanged: false, performanceAction})
      })
    })
  }

  completePublicFilterChange({identityChanged, performanceAction = null}) {
    if (this.restartQueuedPublicIdentityChange(performanceAction)) return

    try {
      this.withPublicSlotsDetached(() => {
        // A view change must discard groups from the old filter even while
        // fresh warnings are pending. Rebuild them once that response arrives.
        if (this.publicParticipationRefresh) this.resetPublicOverlapCollapsedGroups()
        this.apply(null, {publicIdentityChanged: identityChanged})
      })
    } finally {
      this.publicFilterChangePending = false
      this.renderPublicFilterBusy(false)
      this.recordPublicPerformancePhase(performanceAction, "complete")
      window.requestAnimationFrame(() => {
        this.recordPublicPerformancePhase(performanceAction, "visible")
      })
    }
  }

  restartQueuedPublicIdentityChange(performanceAction) {
    const trigger = this.queuedPublicIdentityTrigger
    if (!trigger) return false

    this.queuedPublicIdentityTrigger = null
    const selectedIdentityKey = this.selectedPublicIdentityKey()
    if (
      selectedIdentityKey === this.lastSelectedPublicIdentityKey &&
      selectedIdentityKey === this.publicIdentityStateKey
    ) return false

    this.publicFilterChangePending = false
    this.renderPublicFilterBusy(false)
    this.recordPublicPerformancePhase(performanceAction, "complete")
    window.requestAnimationFrame(() => {
      this.recordPublicPerformancePhase(performanceAction, "visible")
    })
    this.changePublicIdentity({target: trigger})
    return true
  }

  async loadPublicIdentityState({beforeApply = null} = {}) {
    const identityKey = this.selectedPublicIdentityKey()
    if (!identityKey) {
      this.publicIdentityStateAbortController?.abort()
      this.publicIdentityStateAbortController = null
      return this.applyPublicIdentityState({rows: {}}, "")
    }

    if (!this.hasPublicIdentityStateUrlValue || !this.publicIdentityStateUrlValue) {
      throw new Error("Public identity state URL is unavailable")
    }

    this.publicIdentityStateAbortController?.abort()
    const abortController = new AbortController()
    this.publicIdentityStateAbortController = abortController
    const url = new URL(this.publicIdentityStateUrlValue, window.location.origin)
    url.searchParams.set(this.constructor.publicIdentityParam, identityKey)
    url.searchParams.set(this.constructor.publicViewModeParam, this.selectedPublicViewMode(identityKey))

    const timeout = window.setTimeout(() => {
      abortController.abort(new Error("Public identity state timed out"))
    }, 15000)
    try {
      const response = await window.fetch(url.toString(), {
        credentials: "same-origin",
        headers: {Accept: "application/json"},
        signal: abortController.signal,
      })
      if (!response.ok) throw new Error(`Public identity state failed with ${response.status}`)

      const state = await response.json()
      if (beforeApply) await beforeApply
      // Identity can switch away and back before an old response resolves.
      if (abortController.signal.aborted) throw abortController.signal.reason
      if (this.publicIdentityStateAbortController !== abortController) {
        throw new DOMException("Public identity state superseded", "AbortError")
      }
      if (identityKey !== this.selectedPublicIdentityKey()) return
      if (state.identity_key !== identityKey) throw new Error("Public identity state does not match")

      return this.applyPublicIdentityState(state, identityKey)
    } finally {
      window.clearTimeout(timeout)
      if (this.publicIdentityStateAbortController === abortController) {
        this.publicIdentityStateAbortController = null
      }
    }
  }

  applyPublicIdentityState(state, identityKey) {
    const rows = state?.rows || {}
    const rowsToUpdate = this.assignmentRows.filter((row) => {
      return rows[row.dataset.jobId] ||
        row.dataset.publicSignupPreviewProblemIdentities ||
        row.dataset.publicSignupPreviewHintIdentities ||
        row.dataset.publicCurrentOverlapIdentities
    })

    rowsToUpdate.forEach((row) => {
      const rowState = rows[row.dataset.jobId] || {}
      const previewKind = rowState.signup_preview || ""
      const previewTitle = rowState.signup_preview_title || ""
      row.dataset.publicSignupPreviewProblemIdentities = previewKind === "problem" ? identityKey : ""
      row.dataset.publicSignupPreviewHintIdentities = previewKind === "hint" ? identityKey : ""
      row.dataset.publicCurrentOverlapIdentities = rowState.current_overlap ? identityKey : ""

      row.querySelectorAll("[data-people-canvas-public-signup-button]").forEach((button) => {
        button.dataset.publicSignupProblemTitle = previewKind === "problem" ? previewTitle : ""
        button.dataset.publicSignupHintTitle = previewKind === "hint" ? previewTitle : ""
      })
    })

    this.publicIdentityStateKey = identityKey
    delete this.element.dataset.publicIdentityStatePending
    return rowsToUpdate
  }

  handlePublicIdentityStateError(error) {
    if (error?.name === "AbortError") return

    const url = new URL(window.location.href)
    const identityKey = this.selectedPublicIdentityKey()
    if (identityKey) {
      url.searchParams.set(this.constructor.publicIdentityParam, identityKey)
      url.searchParams.set(this.constructor.publicViewModeParam, this.selectedPublicViewMode(identityKey))
    }
    window.location.assign(url.toString())
  }

  cancelPublicFilterChange() {
    if (this.publicFilterChangeFrame !== null) {
      window.cancelAnimationFrame(this.publicFilterChangeFrame)
      this.publicFilterChangeFrame = null
    }

    this.publicFilterChangePending = false
    this.queuedPublicIdentityTrigger = null
    this.setPublicParticipationRefresh(null)
    this.publicIdentityStateAbortController?.abort()
    this.publicIdentityStateAbortController = null
    this.renderPublicFilterBusy(false)
  }

  renderPublicFilterBusy(busy, trigger = null) {
    this.renderPublicFilterTriggerBusy(busy, trigger)
    if (this.hasPublicFilterControlsTarget) {
      this.publicFilterControlsTarget.setAttribute("aria-busy", busy ? "true" : "false")
    }
  }

  publicFilterTriggerFor(event) {
    const eventTarget = event?.target instanceof Element ? event.target : null
    if (eventTarget?.matches("button, select")) return eventTarget

    const activeElement = document.activeElement
    if (activeElement instanceof Element && this.element.contains(activeElement)) return activeElement

    return this.hasPublicIdentitySelectTarget ? this.publicIdentitySelectTarget : null
  }

  renderPublicFilterTriggerBusy(busy, trigger = null) {
    if (!busy) {
      if (this.publicFilterBusyControl) {
        const control = this.publicFilterBusyControl
        control.removeAttribute("aria-busy")
        control.querySelector("[data-public-local-busy-indicator]")?.remove()
        if (control.dataset.publicFilterDisabledDuringUpdate === "true") {
          control.disabled = false
          delete control.dataset.publicFilterDisabledDuringUpdate
        }
      }
      this.publicFilterBusyControl = null
      return
    }

    if (!(trigger instanceof Element)) return
    const control = trigger.closest("button, select") || trigger
    this.publicFilterBusyControl = control
    control.setAttribute("aria-busy", "true")
    if ("disabled" in control && !control.disabled) {
      control.disabled = true
      control.dataset.publicFilterDisabledDuringUpdate = "true"
    }
    if (control.tagName !== "BUTTON" || control.querySelector("[data-public-local-busy-indicator]")) return

    const indicator = document.createElement("i")
    indicator.className = "fa-light fa-spinner-third ml-1.5 animate-spin text-[11px]"
    indicator.dataset.publicLocalBusyIndicator = "true"
    indicator.setAttribute("aria-hidden", "true")
    control.append(indicator)
  }

  togglePlanState(event) {
    const toggle = event.currentTarget
    this.setPlanState(toggle, this.nextPlanState(this.planStateForControl(toggle)))
    this.syncAllPlansControls()
    this.apply()
  }

  toggleAllPlans() {
    const nextState = this.nextAllPlansState()
    this.listedPlanToggleTargets().forEach((toggle) => this.setPlanState(toggle, nextState))
    this.syncAllPlansControls()
    this.apply()
  }

  changePlanState() {
    this.syncAllPlansControls()
    this.apply()
  }

  changeAllPlansState(event) {
    const control = event.currentTarget
    const nextState = control.tagName === "INPUT" ? (control.checked ? "show" : "default") : ((control.value || "show") === "mixed" ? "show" : (control.value || "show"))

    this.listedPlanToggleTargets().forEach((toggle) => this.setPlanState(toggle, nextState))
    this.syncAllPlansControls()
    this.apply()
  }

  changePlanJoinPolicy() {
    this.syncConfigInputs()
  }

  togglePublicSelfSignup() {
    this.apply()
  }

  changePeopleFilter(event) {
    if (event.detail?.kind === "search") {
      const filtersUrl = this.persistFiltersToUrl()
      this.cancelPendingPeopleSearchReload()

      // Once a Turbo visit is underway, waiting for another debounce can let the
      // older response replace this controller before the latest search runs.
      // Starting the newer visit immediately makes Turbo supersede the old one.
      if (this.peopleFilterReloadStarted) {
        this.reloadSchedule(filtersUrl)
        return
      }

      this.peopleSearchReloadTimeout = window.setTimeout(() => {
        this.peopleSearchReloadTimeout = null
        this.reloadSchedule(filtersUrl)
      }, 250)
      return
    }

    this.apply(event)
  }

  changeTimelinePeopleWithoutJobs(event) {
    this.apply(event)
  }

  cancelPendingPeopleSearchReload() {
    window.clearTimeout(this.peopleSearchReloadTimeout)
    this.peopleSearchReloadTimeout = null
  }

  timelineShowAllPeopleEnabled() {
    return this.hasTimelineShowAllPeopleTarget ?
      this.timelineShowAllPeopleTarget.checked :
      this.timelineShowAllPeopleValue
  }

  selectedTimelinePlanningFilter() {
    if (this.hasTimelinePlanningFilterTarget) return this.timelinePlanningFilterTarget.value
    if (this.persistUrlValue && this.currentViewModeValue === "timeline") return ""

    return this.hasTimelinePlanningFilterValue ? this.timelinePlanningFilterValue : ""
  }

  selectedPeopleSearch() {
    const input = this.element.querySelector("[data-people-filter-search]")
    if (input) return input.value.trim()

    return this.hasPeopleSearchValue ? this.peopleSearchValue.trim() : ""
  }

  selectedPeoplePlanningFilters() {
    const filterBar = this.element.querySelector("[data-people-filter-bar]")
    if (!filterBar) return this.hasPeoplePlanningFiltersValue ? this.peoplePlanningFiltersValue : {}

    const filters = {}
    filterBar.querySelectorAll("[data-people-filter-option][data-people-filter-category-id]:checked").forEach((option) => {
      const categoryId = option.dataset.peopleFilterCategoryId
      if (!categoryId) return

      filters[categoryId] ||= []
      if (!filters[categoryId].includes(option.value)) filters[categoryId].push(option.value)
    })

    return filters
  }

  selectedPeoplePlanningEffects() {
    const filterBar = this.element.querySelector("[data-people-filter-bar]")
    const selectedEffects = filterBar ?
      Array.from(filterBar.querySelectorAll("[data-people-filter-effect-option]:checked"), (option) => option.value) :
      (this.hasPeoplePlanningEffectsValue ? this.peoplePlanningEffectsValue : [])
    const effectOrder = ["negative", "positive", "neutral"]

    return effectOrder.filter((effect) => selectedEffects.includes(effect))
  }

  serializedPeoplePlanningFilters() {
    return Object.entries(this.selectedPeoplePlanningFilters())
      .filter(([, selections]) => selections.length > 0)
      .sort(([left], [right]) => Number.parseInt(left, 10) - Number.parseInt(right, 10))
      .map(([categoryId, selections]) => {
        const orderedSelections = [...new Set(selections)].sort((left, right) => {
          if (left === "unset") return 1
          if (right === "unset") return -1
          return Number.parseInt(left, 10) - Number.parseInt(right, 10)
        })

        return `${categoryId}:${orderedSelections.join(",")}`
      })
      .join(";")
  }

  peopleFilterActive() {
    return Boolean(
      this.selectedPeopleSearch() ||
      this.serializedPeoplePlanningFilters() ||
      this.selectedPeoplePlanningEffects().length > 0
    )
  }

  handleTurboRender() {
    this.resetPublicDomIndex()
    this.primePublicDomIndex()
    if (this.publicIdentityEnabledValue) {
      this.currentPublicViewMode = null
      this.publicFocusJobRestored = false
      this.restorePublicFocusJob()
      return
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.apply(null, {persistUrl: false}))
    })
  }

  async handlePublicParticipationUpdate(event) {
    const {eventName, identityKey, jobId} = event.detail || {}
    const form = this.publicSubmissionForm
    const button = this.publicSubmissionButton
    const performanceAction = this.publicSubmissionPerformanceAction || this.publicPerformanceAction
    this.recordPublicPerformancePhase(performanceAction, "complete")
    if (form && button) this.clearPublicSubmissionBusy(form, button)

    if (eventName === "failed" || !identityKey) {
      window.requestAnimationFrame(() => this.recordPublicPerformancePhase(performanceAction, "visible"))
      return
    }

    const row = this.assignmentRows.find((candidate) => candidate.dataset.jobId === jobId)
    if (!row) {
      window.location.reload()
      return
    }
    this.applyPublicParticipationMutation(row, event.detail)

    // Keep the saved participation/capacity even if the person changed while
    // submitting. Their overlap state is loaded by the identity change instead.
    if (identityKey !== this.selectedPublicIdentityKey()) {
      this.applyPublicParticipationState(row)
      window.requestAnimationFrame(() => this.recordPublicPerformancePhase(performanceAction, "visible"))
      return
    }

    const interruptedIdentityChange = this.publicFilterChangePending
    if (interruptedIdentityChange) this.cancelPublicFilterChange()
    if (event.detail.identityState?.identity_key === identityKey) {
      this.setPublicParticipationRefresh(null)
      this.publicIdentityStateAbortController?.abort()
      this.publicIdentityStateAbortController = null
      const identityStateRows = this.applyPublicIdentityState(event.detail.identityState, identityKey)
      if (interruptedIdentityChange) this.apply(null, {publicIdentityChanged: true})
      else this.applyPublicParticipationState(row, identityStateRows)
      const interactionFocus = this.takePublicInteractionFocus(jobId)
      if (interactionFocus) this.restorePublicInteractionPoint(row, interactionFocus)
      // The response already contains fresh warnings. Render once and measure
      // the paint that confirms participation and settles those warnings.
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          this.recordPublicPerformancePhase(performanceAction, "confirmed")
          this.recordPublicPerformancePhase(performanceAction, "visible")
        }, 0)
      })
      return
    }

    const refresh = {identityKey}
    this.setPublicParticipationRefresh(refresh)
    this.publicIdentityStateKey = null
    this.element.dataset.publicIdentityStatePending = "true"
    this.publicIdentityStateAbortController?.abort()
    this.publicIdentityStateAbortController = null
    if (interruptedIdentityChange) this.apply(null, {publicIdentityChanged: true})
    this.applyPublicParticipationState(row)
    const interactionFocus = this.takePublicInteractionFocus(jobId)
    if (interactionFocus) this.restorePublicInteractionPoint(row, interactionFocus)

    // Responses without warning data still confirm the saved participation
    // before applying the separately fetched overlap state.
    const confirmationPaint = new Promise((resolve) => window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        this.recordPublicPerformancePhase(performanceAction, "confirmed")
        resolve()
      }, 0)
    }))

    try {
      const identityStateRows = await this.loadPublicIdentityState({beforeApply: confirmationPaint})
      if (this.publicParticipationRefresh !== refresh || identityKey !== this.selectedPublicIdentityKey()) return

      // Preserve the current position, including any scrolling since confirmation.
      const rowViewportTop = row.getBoundingClientRect().top
      this.setPublicParticipationRefresh(null)
      this.applyPublicParticipationState(row, identityStateRows)
      if (rowViewportTop >= 0 && rowViewportTop < window.innerHeight) {
        this.restorePublicInteractionPoint(row, {rowViewportTop})
      }
      window.requestAnimationFrame(() => {
        window.setTimeout(() => this.recordPublicPerformancePhase(performanceAction, "visible"), 0)
      })
    } catch (error) {
      if (this.publicParticipationRefresh === refresh) this.handlePublicIdentityStateError(error)
    }
  }

  setPublicParticipationRefresh(refresh) {
    // Remember eligibility while controls wait for fresh warnings. A filter can
    // re-render buttons during the wait, so its eligibility updates this map too.
    this.publicParticipationDisabledButtons?.forEach((disabled, button) => {
      button.disabled = disabled
      button.removeAttribute("aria-busy")
    })
    this.publicParticipationDisabledButtons = refresh ? new Map() : null
    this.publicParticipationRefresh = refresh
    if (!refresh) return

    this.element.querySelectorAll("[data-public-signup-corner-button]").forEach((button) => {
      this.setPublicParticipationButtonDisabled(button, button.disabled)
    })
  }

  setPublicParticipationButtonDisabled(button, disabled) {
    this.publicParticipationDisabledButtons?.set(button, disabled)
    button.disabled = disabled || Boolean(this.publicParticipationRefresh)
    if (this.publicParticipationRefresh) button.setAttribute("aria-busy", "true")
    else button.removeAttribute("aria-busy")
  }

  applyPublicParticipationMutation(row, detail) {
    const identityKey = detail.identityKey
    switch (detail.eventName) {
      case "signed_up":
        this.appendIdentityToDataset(row, "personIds", identityKey)
        this.appendIdentityToDataset(row, "publicActivePersonIds", identityKey)
        this.appendIdentityToDataset(row, "publicJoinBlockedIdentityKeys", identityKey)
        this.appendIdentityToDataset(row, "participantKeys", identityKey)
        this.addIdentityToPlanRows(row.dataset.planId, identityKey)
        break
      case "unregistration_requested":
        this.appendIdentityToDataset(row, "pendingUnregistrationIdentityKeys", identityKey)
        break
      case "unregistration_reset":
        this.removeIdentityFromDataset(row, "pendingUnregistrationIdentityKeys", identityKey)
        break
    }

    const lifecycleState = detail.eventName === "signed_up" ? "confirmed" : detail.lifecycleState
    if (lifecycleState) {
      const identityDatasets = {
        publicActivePersonIds: lifecycleState === "confirmed",
        publicInvitedIdentityKeys: lifecycleState === "invited",
        publicRemovedIdentityKeys: ["withdrawn", "released"].includes(lifecycleState),
        publicJoinBlockedIdentityKeys: ["confirmed", "withdrawn", "released"].includes(lifecycleState),
      }
      Object.entries(identityDatasets).forEach(([key, includesIdentity]) => {
        if (includesIdentity) this.appendIdentityToDataset(row, key, identityKey)
        else this.removeIdentityFromDataset(row, key, identityKey)
      })
    }

    const filledSpots = Number.parseInt(detail.staffingFilledSpots || "0", 10)
    const totalSpots = Number.parseInt(detail.staffingTotalSpots || "0", 10)
    const openSpots = Number.parseInt(detail.staffingOpenSpots || "0", 10)
    row.dataset.staffingFilledSpots = String(filledSpots)
    row.dataset.staffingTotalSpots = String(totalSpots)
    row.dataset.staffingOpenSpots = String(openSpots)
    row.dataset.openShift = detail.openShift === "true" ? "true" : "false"
  }

  applyPublicParticipationState(row, identityStateRows = []) {
    const selectedIdentityKey = this.selectedPublicIdentityKey()
    const publicViewMode = this.selectedPublicViewMode(selectedIdentityKey)
    if (publicViewMode === "only_open") {
      this.withPublicSlotsDetached(() => {
        this.apply(null, {publicIdentityChanged: true, preserveExpandedOverlap: true})
      })
      return
    }

    const affectedRows = new Set([row, ...identityStateRows])
    this.assignmentRows.forEach((candidate) => {
      const joinPolicy = candidate.dataset.publicJoinPolicy || "on_space"
      const planEligibilityChanged = joinPolicy === "on_plan" && candidate.dataset.planId === row.dataset.planId
      const slotEligibilityChanged = joinPolicy === "on_slot" && candidate.dataset.slotId === row.dataset.slotId
      if (planEligibilityChanged || slotEligibilityChanged) {
        affectedRows.add(candidate)
      }
    })
    this.renderPublicParticipationButtons(selectedIdentityKey, publicViewMode, affectedRows)
    this.renderPublicPlanHighlights(selectedIdentityKey)

    const sharedFilters = {
      planStates: this.planStates(),
      excludeArchivedPlans: this.excludeArchivedPlansEnabled(),
      showPast: this.hasPastToggleTarget && this.pastToggleTarget.checked,
      selectedSlotTimelineKeys: this.selectedSlotTimelineKeys(),
      includeUntimedSlots: !this.hasUntimedSlotsToggleTarget || this.untimedSlotsToggleTarget.checked,
    }
    const affectedSection = row.closest("[data-people-canvas-filters-target~='section']")
    const affectedSections = new Set([affectedSection].filter(Boolean))
    this.renderStaffingRequirementSummaries(sharedFilters, affectedSections)
    this.renderPublicCommitments(selectedIdentityKey, sharedFilters, {sections: affectedSections})
    if (!this.publicParticipationRefresh) {
      this.renderPublicOverlapCollapsedGroups(selectedIdentityKey, publicViewMode, {preserveExpanded: true})
    }
  }

  appendIdentityToDataset(element, key, identityKey) {
    const identities = this.identityList(element.dataset[key])
    if (identities.includes(identityKey)) return false

    identities.push(identityKey)
    element.dataset[key] = identities.join(",")
    return true
  }

  removeIdentityFromDataset(element, key, identityKey) {
    element.dataset[key] = this.identityList(element.dataset[key])
      .filter((candidate) => candidate !== identityKey)
      .join(",")
  }

  addIdentityToPlanRows(planId, identityKey) {
    this.assignmentRows.filter((row) => row.dataset.planId === planId).forEach((row) => {
      this.appendIdentityToDataset(row, "publicPlanIdentityKeys", identityKey)
      const card = row.closest("[data-people-canvas-card]")
      if (card) this.appendIdentityToDataset(card, "publicPlanIdentityKeys", identityKey)
    })
  }

  preparePublicSubmission(event) {
    if (!this.publicIdentityEnabledValue) return

    const form = event.currentTarget
    if (this.publicSubmissionForm || this.publicParticipationRefresh || this.publicFilterChangePending ||
      form.dataset.publicSubmitting === "true") {
      event.preventDefault()
      return
    }

    const selectedIdentityKey = this.selectedPublicIdentityKey()
    const publicViewMode = this.selectedPublicViewMode(selectedIdentityKey)
    const performanceAction = this.beginPublicPerformanceAction(event.submitter?.dataset.publicPerformanceAction || "public_participation")
    this.publicSubmissionPerformanceAction = performanceAction
    this.publicFocusJobRestored = false
    this.storePublicInteractionFocus(form, event)
    this.renderPublicSubmissionBusy(form, event.submitter)
    form.addEventListener("turbo:before-fetch-request", () => {
      this.recordPublicPerformancePhase(performanceAction, "init")
    }, {once: true})
    window.requestAnimationFrame(() => {
      this.recordPublicPerformancePhase(performanceAction, "reaction")
    })

    form.querySelectorAll("[data-people-canvas-public-identity-input]").forEach((input) => {
      input.value = selectedIdentityKey || ""
    })

    form.querySelectorAll("[data-people-canvas-public-view-mode-input]").forEach((input) => {
      input.value = publicViewMode
    })

    this.savePublicIdentityCookie()
    this.savePublicViewModeCookie()
  }

  renderPublicSubmissionBusy(form, submitter) {
    const button = submitter || form.querySelector("button[type='submit']")
    if (!button || form.dataset.publicSubmitting === "true") return

    form.dataset.publicSubmitting = "true"
    this.publicSubmissionForm = form
    this.publicSubmissionButton = button
    button.disabled = true
    button.setAttribute("aria-busy", "true")
    const icon = button.querySelector("i")
    if (icon) {
      icon.dataset.publicOriginalClass = icon.className
      icon.className = "fa-light fa-spinner-third animate-spin text-[11px]"
    }

    const clearOnFailure = (submitEvent) => {
      if (submitEvent.detail?.success) return
      this.clearPublicSubmissionBusy(form, button)
    }
    form.addEventListener("turbo:submit-end", clearOnFailure, {once: true})

    window.clearTimeout(this.publicSubmissionSafetyTimeout)
    this.publicSubmissionSafetyTimeout = window.setTimeout(() => {
      if (document.contains(form)) this.clearPublicSubmissionBusy(form, button)
    }, 15000)
  }

  clearPublicSubmissionBusy(form, button) {
    delete form.dataset.publicSubmitting
    button.disabled = false
    button.removeAttribute("aria-busy")
    const icon = button.querySelector("i[data-public-original-class]")
    if (icon) {
      icon.className = icon.dataset.publicOriginalClass
      delete icon.dataset.publicOriginalClass
    }
    if (this.publicSubmissionForm === form) {
      this.publicSubmissionForm = null
      this.publicSubmissionPerformanceAction = null
      window.clearTimeout(this.publicSubmissionSafetyTimeout)
      this.publicSubmissionSafetyTimeout = null
    }
    if (this.publicSubmissionButton === button) this.publicSubmissionButton = null
  }

  beginPublicPerformanceAction(action) {
    const performanceAction = {
      action,
      phases: new Set(),
      startedAt: window.performance.now(),
    }
    this.publicPerformanceAction = performanceAction
    window.publicSignupPerformance ||= []
    return performanceAction
  }

  recordPublicPerformancePhase(performanceAction, phase) {
    if (!performanceAction || performanceAction.phases.has(phase)) return

    performanceAction.phases.add(phase)
    window.publicSignupPerformance ||= []
    window.publicSignupPerformance.push({
      action: performanceAction.action,
      phase,
      elapsedMs: Number((window.performance.now() - performanceAction.startedAt).toFixed(2)),
    })
  }

  apply(event, {persistUrl = true, publicIdentityChanged = true, preserveExpandedOverlap = false} = {}) {
    this.cancelPendingPeopleSearchReload()
    this.syncAllPlansControls()
    const planStates = this.planStates()
    const excludeArchivedPlans = this.excludeArchivedPlansEnabled()
    const showPast = this.hasPastToggleTarget && this.pastToggleTarget.checked
    const selectedSlotTimelineKeys = this.selectedSlotTimelineKeys()
    const includeUntimedSlots = !this.hasUntimedSlotsToggleTarget || this.untimedSlotsToggleTarget.checked
    const showSlots = true
    const timelineShowAllPeople = this.timelineShowAllPeopleEnabled()
    const includeCandidateRows = timelineShowAllPeople || this.peopleFilterActive()
    const selectedIdentityKey = this.selectedPublicIdentityKey()
    const publicViewMode = this.selectedPublicViewMode(selectedIdentityKey)
    const sharedFilters = {
      planStates,
      excludeArchivedPlans,
      showPast,
      selectedSlotTimelineKeys,
      includeUntimedSlots,
    }

    const filtersUrl = persistUrl ? this.persistFiltersToUrl() : null
    if (this.shouldReloadForFilterChange(event)) {
      this.reloadSchedule(filtersUrl)
      return
    }

    const rowStates = new Map()
    this.assignmentRows.forEach((row) => {
      const planState = this.planStateForRow(row, planStates, {excludeArchivedPlans})
      const matchesIdentity = planState === "reference" ?
        this.rowMatchesReferenceIdentity(row, selectedIdentityKey, publicViewMode) :
        this.rowMatchesSelectedIdentity(row, selectedIdentityKey, publicViewMode)
      const visible =
        planState !== "excluded" &&
        (showPast || row.dataset.past !== "true") &&
        matchesIdentity &&
        this.rowMatchesTimeFilters(row, {
          selectedSlotTimelineKeys,
          includeUntimedSlots,
        })
      const reference = visible && planState === "reference"
      rowStates.set(row, {visible, reference})

      row.classList.toggle("hidden", !visible)
      row.classList.toggle("opacity-60", reference)
      row.dataset.filterVisible = visible ? "true" : "false"
      row.dataset.filterReference = reference ? "true" : "false"

      row.querySelectorAll("[data-people-canvas-reference-badge]").forEach((badge) => {
        badge.classList.toggle("hidden", !reference)
      })
    })

    this.assignmentGroups.forEach((group) => {
      const visibleRows = this.rowsWithin(group).filter((row) => rowStates.get(row)?.visible)
      const primaryVisibleRows = visibleRows.filter((row) => !rowStates.get(row)?.reference)
      group.classList.toggle("hidden", visibleRows.length === 0)
      group.classList.toggle("opacity-70", visibleRows.length > 0 && primaryVisibleRows.length === 0)
    })

    this.cards.forEach((card) => {
      const totalAssignments = Number(card.dataset.totalAssignments || 0)
      const visibleRows = this.rowsWithin(card).filter((row) => rowStates.get(row)?.visible)
      const primaryVisibleRows = visibleRows.filter((row) => !rowStates.get(row)?.reference)
      const referenceVisibleRows = visibleRows.filter((row) => rowStates.get(row)?.reference)
      const visible = this.cardVisible(card, {
        totalAssignments,
        primaryVisibleRows,
        referenceVisibleRows,
        selectedIdentityKey,
        publicViewMode,
      })
      const referenceOnly = visible && primaryVisibleRows.length === 0 && referenceVisibleRows.length > 0
      const scheduleReferenceOnly = referenceOnly && this.scopeFor(card) === "schedule"

      card.classList.toggle("hidden", !visible)
      card.classList.toggle("opacity-70", referenceOnly && !scheduleReferenceOnly)
      card.classList.toggle("opacity-40", scheduleReferenceOnly)
      card.classList.toggle("saturate-50", scheduleReferenceOnly)
      card.classList.toggle("ring-sky-300", visible && selectedIdentityKey && card.dataset.personIdentityKey === selectedIdentityKey)
      card.classList.toggle("bg-sky-50/80", visible && selectedIdentityKey && card.dataset.personIdentityKey === selectedIdentityKey)
      card.classList.toggle("dark:bg-sky-900/20", visible && selectedIdentityKey && card.dataset.personIdentityKey === selectedIdentityKey)

      const countElement = card.querySelector("[data-people-canvas-card-count]")
      if (countElement) {
        countElement.textContent = this.cardCountLabel(card, {
          primaryCount: visible ? primaryVisibleRows.length : 0,
          referenceCount: visible ? referenceVisibleRows.length : 0,
        })
      }
    })

    this.sections.forEach((section) => {
      const visibleCards = this.cardsWithin(section).filter((card) => !card.classList.contains("hidden")).length
      const visibleRows = this.rowsWithin(section).filter((row) => rowStates.get(row)?.visible)
      const primaryVisibleRows = visibleRows.filter((row) => !rowStates.get(row)?.reference)
      const visible = section.dataset.schedulePersonRow === "true" ?
        (includeCandidateRows || primaryVisibleRows.length > 0) :
        visibleCards > 0

      section.classList.toggle("hidden", !visible)

      const countElement = section.querySelector("[data-people-canvas-section-count]")
      if (countElement) {
        this.updateSectionCountElement(countElement, visibleCards, visibleRows)
      }
    })

    this.renderStaffingRequirementSummaries(sharedFilters)

    this.emptyStates.forEach((emptyState) => {
      const scope = this.scopeFor(emptyState)
      const hasVisibleCard = this.cards.some(
        (card) => this.scopeFor(card) === scope && !card.classList.contains("hidden")
      )
      const hasVisibleSchedulePerson = scope === "schedule" && this.sections.some(
        (section) => section.dataset.schedulePersonRow === "true" && !section.classList.contains("hidden")
      )
      const sectionVisible = scope === "slots" ? showSlots : true
      emptyState.classList.toggle("hidden", hasVisibleCard || hasVisibleSchedulePerson || !sectionVisible)
    })

    this.renderPublicIdentity(selectedIdentityKey, publicViewMode)
    this.renderPublicCalendarLinks(selectedIdentityKey)
    this.schedulePublicCalendarSuggestion(selectedIdentityKey)
    if (!this.publicIdentityEnabledValue || publicIdentityChanged) {
      this.renderHighlightedPeople(selectedIdentityKey)
      this.renderPublicPlanHighlights(selectedIdentityKey)
      this.renderPublicParticipationButtons(selectedIdentityKey, publicViewMode)
      this.renderPublicCommitments(selectedIdentityKey, {
        ...sharedFilters,
      })
    }
    if (!this.publicParticipationRefresh) {
      this.renderPublicOverlapCollapsedGroups(selectedIdentityKey, publicViewMode, {preserveExpanded: preserveExpandedOverlap})
    }
    this.renderRootSections({showSlots})
    if (!this.publicIdentityEnabledValue) {
      this.renderPublicTextScale()
      this.renderPublicJoinPolicyControls()
      this.renderSlotsGridWidth()
      this.syncConfigInputs()
      this.updateStickyOffset()
    }
    this.restorePublicFocusJob()
  }

  setupStickyHeaderObserver() {
    if (!this.hasStickyHeaderTarget) return

    this.updateStickyOffset()
    window.addEventListener("resize", this.boundUpdateStickyOffset)

    if (!window.ResizeObserver) return

    this.stickyHeaderObserver = new window.ResizeObserver(() => {
      this.updateStickyOffset()
    })
    this.stickyHeaderObserver.observe(this.stickyHeaderTarget)
    const mobileToolbar = this.stickyHeaderTarget.querySelector("[data-public-mobile-toolbar]")
    if (mobileToolbar) this.stickyHeaderObserver.observe(mobileToolbar)
  }

  teardownStickyHeaderObserver() {
    window.removeEventListener("resize", this.boundUpdateStickyOffset)
    this.stickyHeaderObserver?.disconnect()
    this.stickyHeaderObserver = null

    if (!this.hasStickyHeaderTarget) {
      this.element.style.removeProperty("--public-share-sticky-offset")
    }
  }

  updateStickyOffset() {
    if (!this.hasStickyHeaderTarget) {
      this.element.style.removeProperty("--public-share-sticky-offset")
      return
    }

    const mobileToolbar = this.stickyHeaderTarget.querySelector("[data-public-mobile-toolbar]")
    const offsetElement = [this.stickyHeaderTarget, mobileToolbar].find((element) => {
      if (!element || element.offsetHeight === 0) return false

      return ["sticky", "fixed"].includes(window.getComputedStyle(element).position)
    })

    if (!offsetElement) {
      this.element.style.setProperty("--public-share-sticky-offset", "0px")
      return
    }

    const styles = window.getComputedStyle(offsetElement)
    const marginBottom = Number.parseFloat(styles.marginBottom || "0") || 0
    const offset = offsetElement.offsetHeight + marginBottom
    this.element.style.setProperty("--public-share-sticky-offset", `${offset}px`)
  }

  restorePublicFocusJob() {
    if (!this.publicIdentityEnabledValue || this.publicFocusJobRestored) return

    const focusJobId = this.publicFocusJobIdValue || ""
    const interactionFocus = this.takePublicInteractionFocus(focusJobId)
    const restoredJobId = focusJobId || interactionFocus?.jobId || ""
    if (!restoredJobId) return

    const row = this.assignmentRows.find((candidate) => candidate.dataset.jobId === restoredJobId)
    this.publicFocusJobRestored = true
    if (!row) return

    if (!interactionFocus) return

    this.restorePublicInteractionPoint(row, interactionFocus)
    this.stabilizePublicInteractionPoint(restoredJobId, interactionFocus)
  }

  stabilizePublicInteractionPoint(jobId, interactionFocus) {
    const deadline = window.performance.now() + this.constructor.publicInteractionSettlingDuration
    const restoreWhileSettling = () => {
      if (window.performance.now() >= deadline) return

      const currentRow = this.assignmentRows.find((candidate) => candidate.dataset.jobId === jobId)
      if (currentRow) this.restorePublicInteractionPoint(currentRow, interactionFocus)
      window.requestAnimationFrame(restoreWhileSettling)
    }

    window.requestAnimationFrame(restoreWhileSettling)
  }

  storePublicInteractionFocus(form, event) {
    const row = (event.submitter || form).closest("[data-people-canvas-assignment-row]")
    if (!row) return

    const rect = row.getBoundingClientRect()
    const submitter = event.submitter || form.querySelector("button[type='submit']")
    const submitterRect = submitter?.getBoundingClientRect()
    const viewportX = Number.isFinite(event.clientX) ? event.clientX : (submitterRect ? submitterRect.left + (submitterRect.width / 2) : rect.right)
    const viewportY = Number.isFinite(event.clientY) ? event.clientY : (submitterRect ? submitterRect.top + (submitterRect.height / 2) : rect.bottom)
    const payload = {
      jobId: row.dataset.jobId || "",
      viewportY,
      viewportX,
      rowViewportTop: rect.top,
      bottomOffset: rect.bottom - viewportY,
      rightOffset: rect.right - viewportX,
      at: Date.now(),
    }
    try {
      window.sessionStorage.setItem(this.publicInteractionFocusStorageKey(), JSON.stringify(payload))
    } catch (_error) {
    }
  }

  takePublicInteractionFocus(expectedJobId = "") {
    const key = this.publicInteractionFocusStorageKey()
    let rawPayload = null

    try {
      rawPayload = window.sessionStorage.getItem(key)
      window.sessionStorage.removeItem(key)
    } catch (_error) {
      return null
    }

    if (!rawPayload) return null

    let payload = null
    try {
      payload = JSON.parse(rawPayload)
    } catch (_error) {
      return null
    }

    if (expectedJobId && payload.jobId !== expectedJobId) return null
    if (Date.now() - Number(payload.at || 0) > 120000) return null
    if (!Number.isFinite(payload.viewportY) || !Number.isFinite(payload.viewportX)) return null
    if (!Number.isFinite(payload.bottomOffset) || !Number.isFinite(payload.rightOffset)) return null

    return payload
  }

  publicInteractionFocusStorageKey() {
    const url = new URL(window.location.href)
    return `people-canvas-public-interaction:${url.pathname}:${url.searchParams.get("code") || ""}`
  }

  restorePublicInteractionPoint(row, interactionFocus) {
    if (!interactionFocus) return false

    if (Number.isFinite(interactionFocus.rowViewportTop)) {
      const rect = row.getBoundingClientRect()
      window.scrollBy({top: rect.top - interactionFocus.rowViewportTop, left: 0, behavior: "instant"})
      this.ensurePublicInteractionRowVisible(row)
      return true
    }

    const anchor = this.visiblePublicInteractionAnchor(row)
    if (anchor) {
      const rect = anchor.getBoundingClientRect()
      const anchorY = rect.top + (rect.height / 2)
      window.scrollBy({top: anchorY - interactionFocus.viewportY, left: 0, behavior: "instant"})
      this.ensurePublicInteractionRowVisible(row)
      return true
    }

    const rect = row.getBoundingClientRect()
    const targetY = interactionFocus.viewportY + interactionFocus.bottomOffset
    const deltaY = rect.bottom - targetY

    window.scrollBy({top: deltaY, left: 0, behavior: "instant"})
    this.ensurePublicInteractionRowVisible(row)
    return true
  }

  ensurePublicInteractionRowVisible(row) {
    const rect = row.getBoundingClientRect()
    if (rect.bottom > 0 && rect.top < window.innerHeight) return

    row.scrollIntoView({block: "center", behavior: "instant"})
  }

  visiblePublicInteractionAnchor(row) {
    return Array.from(row.querySelectorAll("[data-public-signup-corner-button]")).find((button) => {
      const rect = button.getBoundingClientRect()
      const styles = window.getComputedStyle(button)

      return rect.width > 0 &&
        rect.height > 0 &&
        styles.display !== "none" &&
        styles.visibility !== "hidden" &&
        !button.disabled
    })
  }

  get assignmentGroups() {
    return this.publicDomIndex?.assignmentGroups || Array.from(this.element.querySelectorAll("[data-people-canvas-assignment-group]"))
  }

  get assignmentRows() {
    return this.publicDomIndex?.assignmentRows || Array.from(this.element.querySelectorAll("[data-people-canvas-assignment-row]"))
  }

  get sections() {
    return this.publicDomIndex?.sections || this.sectionTargets
  }

  get cards() {
    return this.publicDomIndex?.cards || this.cardTargets
  }

  get staffingSummaryRows() {
    return this.publicDomIndex?.staffingSummaryRows || Array.from(this.element.querySelectorAll("[data-people-canvas-staffing-summary-row]"))
  }

  get emptyStates() {
    return this.publicDomIndex?.emptyStates || this.emptyStateTargets
  }

  primePublicDomIndex() {
    if (!this.publicIdentityEnabledValue || this.publicDomIndex) return

    this.publicDomIndex = {
      assignmentGroups: Array.from(this.element.querySelectorAll("[data-people-canvas-assignment-group]")),
      assignmentRows: Array.from(this.element.querySelectorAll("[data-people-canvas-assignment-row]")),
      sections: this.sectionTargets,
      cards: this.cardTargets,
      emptyStates: this.emptyStateTargets,
      staffingSummaryRows: Array.from(this.element.querySelectorAll("[data-people-canvas-staffing-summary-row]")),
      staffingSummaryElements: Array.from(this.element.querySelectorAll("[data-slot-group-staffing-summary]")),
      linkedPeople: Array.from(this.element.querySelectorAll("[data-people-canvas-linked-person]")),
      publicPlanElements: Array.from(this.element.querySelectorAll("[data-public-plan-identity-keys]")),
      publicPlanBadges: Array.from(this.element.querySelectorAll("[data-public-plan-badge]")),
      slotsRoot: this.element.querySelector("[data-people-canvas-root-section='slots']"),
    }
  }

  resetPublicDomIndex() {
    this.publicDomIndex = null
    this.publicRowsByElement = null
    this.publicCardsByElement = null
  }

  withPublicSlotsDetached(callback) {
    const slotsRoot = this.publicDomIndex?.slotsRoot
    const parent = slotsRoot?.parentNode
    if (!slotsRoot || !parent) return callback()

    const scrollPosition = {left: window.scrollX, top: window.scrollY}
    const rect = slotsRoot.getBoundingClientRect()
    const styles = window.getComputedStyle(slotsRoot)
    const placeholder = document.createElement("div")
    placeholder.dataset.peopleCanvasSlotsPlaceholder = "true"
    placeholder.setAttribute("aria-hidden", "true")
    placeholder.style.boxSizing = "border-box"
    placeholder.style.display = styles.display === "contents" ? "block" : styles.display
    placeholder.style.height = `${rect.height}px`
    placeholder.style.marginTop = styles.marginTop
    placeholder.style.marginRight = styles.marginRight
    placeholder.style.marginBottom = styles.marginBottom
    placeholder.style.marginLeft = styles.marginLeft
    parent.insertBefore(placeholder, slotsRoot)
    slotsRoot.remove()
    try {
      return callback()
    } finally {
      parent.replaceChild(slotsRoot, placeholder)
      window.scrollTo({...scrollPosition, behavior: "instant"})
    }
  }

  publicSlotsQuerySelectorAll(selector) {
    const root = this.publicDomIndex?.slotsRoot || this.element
    return root.querySelectorAll(selector)
  }

  rowsWithin(element) {
    this.publicRowsByElement ||= new WeakMap()
    let rows = this.publicRowsByElement.get(element)
    if (!rows) {
      rows = Array.from(element.querySelectorAll("[data-people-canvas-assignment-row]"))
      this.publicRowsByElement.set(element, rows)
    }
    return rows
  }

  cardsWithin(element) {
    this.publicCardsByElement ||= new WeakMap()
    let cards = this.publicCardsByElement.get(element)
    if (!cards) {
      cards = Array.from(element.querySelectorAll("[data-people-canvas-card]"))
      this.publicCardsByElement.set(element, cards)
    }
    return cards
  }

  excludeArchivedPlansEnabled() {
    return this.hasExcludeArchivedPlansToggleTarget ? this.excludeArchivedPlansToggleTarget.checked : this.excludeArchivedPlansValue
  }

  listedPlanToggleTargets() {
    return this.planToggleTargets.filter((toggle) => !toggle.classList.contains("hidden"))
  }

  visibleRowsWithin(element) {
    return this.rowsWithin(element).filter(
      (row) => row.dataset.filterVisible === "true"
    )
  }

  planStates() {
    return new Map(this.planToggleTargets.filter((toggle) => {
      const state = this.planStateForControl(toggle)
      return state !== "default"
    }).map((toggle) => [toggle.dataset.planId, this.planStateForControl(toggle)]))
  }

  planStateForRow(row, planStates, {excludeArchivedPlans} = {}) {
    return this.planStateForPlanId(row.dataset.planId, planStates, {
      archived: row.dataset.planArchived === "true",
      excludeArchivedPlans,
    })
  }

  planStateForPlanId(planId, planStates, {archived = false, excludeArchivedPlans = this.excludeArchivedPlansEnabled()} = {}) {
    const state = planStates.get(planId) || "default"
    const selectedPlanFilter = Array.from(planStates.values()).some((planState) => planState === "show" || planState === "reference")

    if (selectedPlanFilter) {
      switch (state) {
        case "show":
          return "included"
        case "reference":
          return "reference"
        default:
          return "excluded"
      }
    }

    if (excludeArchivedPlans && archived && state === "default") {
      return "excluded"
    }

    return state === "hide" ? "excluded" : "included"
  }

  selectedSlotTimelineKeys() {
    return new Set(this.slotTimelineToggleTargets.filter((toggle) => toggle.checked).map((toggle) => toggle.value))
  }

  rowMatchesTimeFilters(row, { selectedSlotTimelineKeys, includeUntimedSlots }) {
    const slotTimed = row.dataset.slotTimed === "true"

    if (!slotTimed) {
      return includeUntimedSlots
    }

    const rowSlotTimelineKeys = this.rowSlotTimelineKeys(row)
    if (selectedSlotTimelineKeys.size > 0 && !rowSlotTimelineKeys.some((key) => selectedSlotTimelineKeys.has(key))) {
      return false
    }

    return true
  }

  rowSlotTimelineKeys(row) {
    return (row.dataset.slotTimelineFilterKeys || row.dataset.slotTimelineKey || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
  }

  rowMatchesSharedFilters(row, { planStates, excludeArchivedPlans, showPast, selectedSlotTimelineKeys, includeUntimedSlots }) {
    return this.planStateForRow(row, planStates, {excludeArchivedPlans}) !== "excluded" &&
      (showPast || row.dataset.past !== "true") &&
      this.rowMatchesTimeFilters(row, {
        selectedSlotTimelineKeys,
        includeUntimedSlots,
      })
  }

  rowMatchesSelectedIdentity(row, selectedIdentityKey, publicViewMode) {
    if (this.scopeFor(row) !== "slots") return true
    const rowPersonIds = this.rowPersonIds(row)

    if (!selectedIdentityKey) return publicViewMode === "only_open" ? row.dataset.openShift === "true" : true
    if (publicViewMode === "all") return true
    if (publicViewMode === "only_open") {
      return this.rowJoinEligible(row, selectedIdentityKey)
    }

    return rowPersonIds.includes(selectedIdentityKey) || (publicViewMode === "mine_and_open" && this.rowJoinEligible(row, selectedIdentityKey))
  }

  rowMatchesReferenceIdentity(row, selectedIdentityKey, publicViewMode) {
    if (this.scopeFor(row) !== "slots") return true
    if (!this.publicIdentityEnabledValue) return true
    if (!selectedIdentityKey || publicViewMode === "only_open") return false

    return this.rowPersonIds(row).includes(selectedIdentityKey)
  }

  rowPersonIds(row) {
    return this.identityList(row.dataset.personIds)
  }

  rowActivePersonIds(row) {
    const identityKeys = row.dataset.publicActivePersonIds
    return this.identityList(identityKeys == null ? row.dataset.personIds : identityKeys)
  }

  rowJoinBlockedIdentityKeys(row) {
    const identityKeys = row.dataset.publicJoinBlockedIdentityKeys
    return this.identityList(identityKeys == null ? row.dataset.personIds : identityKeys)
  }

  rowInvitedIdentityKeys(row) {
    return this.identityList(row.dataset.publicInvitedIdentityKeys)
  }

  rowRemovedIdentityKeys(row) {
    return this.identityList(row.dataset.publicRemovedIdentityKeys)
  }

  identityList(value) {
    return (value || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
  }

  rowPendingUnregistrationIdentityKeys(row) {
    return (row.dataset.pendingUnregistrationIdentityKeys || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
  }

  rowSignupPreviewProblemIdentityKeys(row) {
    return (row.dataset.publicSignupPreviewProblemIdentities || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
  }

  rowSignupPreviewHintIdentityKeys(row) {
    return (row.dataset.publicSignupPreviewHintIdentities || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
  }

  rowCurrentOverlapIdentityKeys(row) {
    return (row.dataset.publicCurrentOverlapIdentities || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
  }

  rowCommitmentSecondsRange(row) {
    const fallbackSeconds = Number.parseInt(row.dataset.commitmentSeconds || "0", 10)
    const minimumSeconds = Number.parseInt(row.dataset.commitmentMinimumSeconds || row.dataset.commitmentSeconds || "0", 10)
    const maximumSeconds = Number.parseInt(row.dataset.commitmentMaximumSeconds || row.dataset.commitmentSeconds || "0", 10)
    const fallback = Number.isFinite(fallbackSeconds) ? fallbackSeconds : 0
    const minimum = Number.isFinite(minimumSeconds) ? minimumSeconds : fallback
    const maximum = Number.isFinite(maximumSeconds) ? maximumSeconds : fallback

    return {
      minimum: Math.max(0, minimum),
      maximum: Math.max(0, maximum),
    }
  }

  rowPotentiallyOverlappingForSelectedIdentity(row, selectedIdentityKey, publicViewMode) {
    if (row.dataset.publicOverlapHiddenCandidate !== "true") return false
    const rowPersonIds = this.rowPersonIds(row)
    if (!this.rowJoinEligible(row, selectedIdentityKey)) return false
    if (publicViewMode === "mine_and_open" && rowPersonIds.includes(selectedIdentityKey)) return false

    return true
  }

  rowJoinEligible(row, selectedIdentityKey) {
    if (!selectedIdentityKey || !this.publicSelfSignupEnabledValue) return false
    if (this.rowJoinBlockedIdentityKeys(row).includes(selectedIdentityKey)) return false
    if (this.rowInvitedIdentityKeys(row).includes(selectedIdentityKey)) return true
    if (row.dataset.openShift !== "true") return false

    switch (row.dataset.publicJoinPolicy || "on_space") {
      case "noone":
        return false
      case "on_space":
      case "everyone":
        return true
      case "on_plan":
        return this.identityParticipatesOnPlan(selectedIdentityKey, row.dataset.planId)
      case "on_slot":
        return this.identityParticipatesOnSlot(selectedIdentityKey, row.dataset.slotId)
    }

    return true
  }

  identityParticipatesOnPlan(selectedIdentityKey, planId) {
    return this.assignmentRows.some((row) =>
      row.dataset.planId === planId && this.rowActivePersonIds(row).includes(selectedIdentityKey)
    )
  }

  identityParticipatesOnSlot(selectedIdentityKey, slotId) {
    return this.assignmentRows.some((row) =>
      row.dataset.slotId === slotId && this.rowActivePersonIds(row).includes(selectedIdentityKey)
    )
  }

  cardVisible(card, { totalAssignments, primaryVisibleRows, referenceVisibleRows }) {
    if (totalAssignments === 0) {
      return false
    }

    if (primaryVisibleRows.length > 0) return true

    return card.dataset.showReferenceOnly === "true" && referenceVisibleRows.length > 0
  }
  renderPlanToggle(toggle) {
    if (toggle.tagName !== "BUTTON") return

    const state = toggle.dataset.planState || "show"
    const box = toggle.querySelector("[data-people-canvas-plan-toggle-box]")
    const icon = toggle.querySelector("[data-people-canvas-plan-toggle-icon]")

    const config = this.planToggleConfig(state)

    toggle.className = config.buttonClassName
    toggle.setAttribute("aria-checked", config.ariaChecked)
    toggle.setAttribute("title", `${toggle.dataset.planName}: ${config.title}`)

    if (box) {
      box.className = config.boxClassName
    }

    if (icon) {
      icon.className = config.iconClassName
    }
  }

  renderAllPlansToggle() {
    if (!this.hasAllPlansToggleTarget) return

    const state = this.currentAllPlansState()
    if (this.allPlansToggleTarget.tagName === "INPUT") {
      this.allPlansToggleTarget.checked = state === "show"
      this.allPlansToggleTarget.indeterminate = state === "mixed"
      return
    }

    this.setPlanState(this.allPlansToggleTarget, state)
  }

  syncAllPlansControls() {
    this.planToggleTargets.forEach((toggle) => this.renderPlanToggle(toggle))
    this.syncArchivedPlanControls()
    this.renderAllPlansToggle()
  }

  syncArchivedPlanControls() {
    const excludeArchivedPlans = this.excludeArchivedPlansEnabled()

    this.planToggleTargets.forEach((toggle) => {
      const archived = toggle.dataset.planArchived === "true"
      const explicitlySelected = ["show", "reference"].includes(this.planStateForControl(toggle))
      toggle.classList.toggle("hidden", excludeArchivedPlans && archived && !explicitlySelected)
    })
  }

  renderPublicJoinPolicyControls() {
    const enabled = !this.hasAllowPublicSelfSignupToggleTarget || this.allowPublicSelfSignupToggleTarget.checked
    const planStates = this.planStates()

    if (this.hasPublicJoinPolicySectionTarget) {
      this.publicJoinPolicySectionTarget.classList.toggle("hidden", !enabled)
    }

    if (!this.hasPublicJoinPolicyGroupTarget) return

    this.publicJoinPolicyGroupTargets.forEach((element) => {
      const planId = element.dataset.planId
      const visible = enabled && (!planId || this.planStateForPlanId(planId, planStates, {archived: element.dataset.planArchived === "true"}) !== "excluded")
      element.classList.toggle("hidden", !visible)
    })

    this.planJoinPolicyTargets.forEach((select) => {
      select.disabled = !enabled
    })
  }

  cardCountLabel(card, { primaryCount, referenceCount = 0 }) {
    const singular = card.dataset.cardCountSingular || "item"
    const plural = card.dataset.cardCountPlural || `${singular}s`
    const countMode = card.dataset.cardCountMode || "split"

    if (countMode === "total") {
      const totalCount = primaryCount + referenceCount
      return `${totalCount} ${totalCount === 1 ? singular : plural}`
    }

    const primaryLabel = `${primaryCount} ${primaryCount === 1 ? singular : plural}`
    if (referenceCount === 0) return primaryLabel

    const referenceSingular = this.translationFor("reference_labels.one", "reference")
    const referencePlural = this.translationFor("reference_labels.other", "references")

    return `${primaryLabel} + ${referenceCount} ${referenceCount === 1 ? referenceSingular : referencePlural}`
  }

  sectionCountLabel(countElement, count, visibleRows = []) {
    if (countElement.dataset.sectionCountMode === "distinct-assignments") {
      return this.distinctAssignmentCountLabel(countElement, visibleRows)
    }

    const singular = countElement.dataset.sectionCountSingular || "item"
    const plural = countElement.dataset.sectionCountPlural || `${singular}s`
    return `${count} ${count === 1 ? singular : plural}`
  }

  updateSectionCountElement(countElement, count, visibleRows = []) {
    const label = this.sectionCountLabel(countElement, count, visibleRows)
    countElement.setAttribute("aria-label", label)

    if (countElement.dataset.sectionCountMode !== "distinct-assignments") {
      countElement.textContent = label
      return
    }

    const counts = this.distinctAssignmentCounts(visibleRows)
    const jobCountValue = countElement.querySelector("[data-people-canvas-section-job-count]")
    const personCountValue = countElement.querySelector("[data-people-canvas-section-person-count]")

    if (!jobCountValue || !personCountValue) {
      countElement.textContent = label
      return
    }

    jobCountValue.textContent = counts.jobCount
    personCountValue.textContent = counts.personCount
  }

  distinctAssignmentCountLabel(countElement, visibleRows) {
    const counts = this.distinctAssignmentCounts(visibleRows)
    const jobIds = new Set()
    const jobLabel = this.countLabel(
      counts.jobCount,
      countElement.dataset.jobCountSingular || "job",
      countElement.dataset.jobCountPlural || "jobs"
    )
    const personLabel = this.countLabel(
      counts.personCount,
      countElement.dataset.personCountSingular || "person",
      countElement.dataset.personCountPlural || "people"
    )

    return `${jobLabel} · ${personLabel}`
  }

  distinctAssignmentCounts(visibleRows) {
    const jobIds = new Set()
    const participantKeys = new Set()

    visibleRows.forEach((row) => {
      if (row.dataset.jobId) {
        jobIds.add(row.dataset.jobId)
      }

      const rowParticipantKeys = (row.dataset.participantKeys || "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean)

      rowParticipantKeys.forEach((key) => participantKeys.add(key))
    })

    return {
      jobCount: jobIds.size,
      personCount: participantKeys.size,
    }
  }

  countLabel(count, singular, plural) {
    return `${count} ${count === 1 ? singular : plural}`
  }

  renderStaffingRequirementSummaries(filters, sections = null) {
    this.renderDashboardStaffingSummary(filters)

    const summaryElements = this.publicDomIndex?.staffingSummaryElements || this.element.querySelectorAll("[data-slot-group-staffing-summary]")
    summaryElements.forEach((summaryElement) => {
      const section = summaryElement.closest("[data-people-canvas-filters-target~='section']")
      if (sections && !sections.has(section)) return
      const visibleRows = section ? this.visibleRowsWithin(section) : []

      this.renderStaffingSummaryElement(summaryElement, this.staffingRequirementSummaryForRows(visibleRows))
    })
  }

  renderDashboardStaffingSummary(filters) {
    const summaryElement = this.element.querySelector("[data-space-staffing-summary]")
    if (!summaryElement) return

    const visibleRows = this.staffingSummaryRows.filter((row) => this.rowMatchesSharedFilters(row, filters))
    this.renderStaffingSummaryElement(summaryElement, this.staffingRequirementSummaryForRows(visibleRows))
  }

  renderStaffingSummaryElement(summaryElement, summary) {
    const percentageElement = summaryElement.querySelector("[data-staffing-summary-percentage]")
    const openElement = summaryElement.querySelector("[data-staffing-summary-open]")
    const statusElement = summaryElement.querySelector("[data-staffing-summary-status]")

    if (!summary) {
      if (summaryElement.matches("[data-slot-group-staffing-summary]")) {
        summaryElement.classList.add("hidden")
      }

      this.clearStaffingSummaryStateClasses(summaryElement)
      if (percentageElement) this.clearStaffingSummaryStateClasses(percentageElement)
      this.replaceClassList(summaryElement, summaryElement.dataset.emptyClasses, true)
      if (percentageElement) this.setTextIfChanged(percentageElement, "—")
      if (statusElement) this.setTextIfChanged(statusElement, summaryElement.dataset.emptyLabel || "")
      summaryElement.removeAttribute("aria-label")
      return
    }

    const complete = summary.openSpots === 0
    const percentLabel = this.staffingSummaryPercentLabel(summaryElement, summary)
    const openLabel = this.staffingSummaryOpenLabel(summaryElement, summary)

    summaryElement.classList.remove("hidden")
    this.replaceClassList(summaryElement, summaryElement.dataset.emptyClasses, false)
    this.applyStaffingSummaryStateClasses(summaryElement, complete)

    if (percentageElement) {
      this.setTextIfChanged(percentageElement, `${summary.fillPercentage}%`)
      this.applyStaffingSummaryStateClasses(percentageElement, complete)
    }

    if (openElement) {
      this.setTextIfChanged(openElement, openLabel)
      openElement.classList.toggle("hidden", complete)
    }

    if (statusElement) {
      this.setTextIfChanged(statusElement, this.formatStaffingSummaryTemplate(
        summaryElement.dataset.statusTemplate || "",
        {
          filled: summary.filledSpots,
          total: summary.totalSpots,
          open: summary.openSpots,
        }
      ))
    }

    summaryElement.setAttribute("aria-label", complete ? percentLabel : `${percentLabel} · ${openLabel}`)
  }

  staffingRequirementSummaryForRows(rows) {
    const rowsByAssignment = new Map()

    rows.forEach((row, index) => {
      rowsByAssignment.set(row.dataset.jobId || `row-${index}`, row)
    })

    let hasRequirement = false
    let filledSpots = 0
    let totalSpots = 0
    let openSpots = 0

    rowsByAssignment.forEach((row) => {
      hasRequirement = hasRequirement || row.dataset.staffingRequirementEnabled === "true"
      filledSpots += this.numberFromDataset(row.dataset.staffingFilledSpots)
      totalSpots += this.numberFromDataset(row.dataset.staffingTotalSpots)
      openSpots += this.numberFromDataset(row.dataset.staffingOpenSpots)
    })

    if (!hasRequirement) return null

    return {
      filledSpots,
      totalSpots,
      openSpots,
      fillPercentage: totalSpots > 0 ? Math.round((filledSpots / totalSpots) * 100) : 100,
    }
  }

  staffingSummaryPercentLabel(summaryElement, summary) {
    const template = summaryElement.dataset.percentTemplate || "__count__%"
    return this.formatStaffingSummaryTemplate(template, {count: summary.fillPercentage})
  }

  staffingSummaryOpenLabel(summaryElement, summary) {
    const template = summary.openSpots === 1 ?
      summaryElement.dataset.openOneTemplate :
      summaryElement.dataset.openOtherTemplate

    return this.formatStaffingSummaryTemplate(template || "__count__ open", {count: summary.openSpots})
  }

  formatStaffingSummaryTemplate(template, values) {
    return Object.entries(values).reduce((memo, [key, value]) => {
      const pattern = new RegExp(`(%\\{${key}\\}|__${key}__)`, "g")
      return memo.replace(pattern, String(value))
    }, template || "")
  }

  applyStaffingSummaryStateClasses(element, complete) {
    this.replaceClassList(element, element.dataset.completeClasses, complete)
    this.replaceClassList(element, element.dataset.incompleteClasses, !complete)
  }

  clearStaffingSummaryStateClasses(element) {
    this.replaceClassList(element, element.dataset.completeClasses, false)
    this.replaceClassList(element, element.dataset.incompleteClasses, false)
  }

  numberFromDataset(value) {
    const number = Number.parseInt(value || "0", 10)
    return Number.isFinite(number) ? number : 0
  }

  renderSlotsGridWidth() {
    const slotsGrid = this.element.querySelector("[data-people-canvas-slots-grid]")
    if (!slotsGrid) return

    const slotsRootSection = this.element.querySelector("[data-people-canvas-root-section='slots']")
    if (slotsRootSection?.classList.contains("hidden")) {
      slotsGrid.style.removeProperty("grid-template-columns")
      delete slotsGrid.dataset.singleVisible
      return
    }

    const visibleSlotSections = this.sections.filter(
      (section) => this.scopeFor(section) === "slots" && !section.classList.contains("hidden")
    ).length

    if (visibleSlotSections <= 1) {
      slotsGrid.style.gridTemplateColumns = "minmax(0, 1fr)"
      slotsGrid.dataset.singleVisible = "true"
      return
    }

    slotsGrid.style.removeProperty("grid-template-columns")
    delete slotsGrid.dataset.singleVisible
  }

  scopeFor(element) {
    return element.dataset.canvasScope || element.closest("[data-canvas-scope]")?.dataset.canvasScope || "default"
  }

  persistFiltersToUrl() {
    if (!this.persistUrlValue) return null

    const url = new URL(window.location.href)
    const storedPlanStates = Array.from(this.planStates(), ([planId, state]) => `${planId}:${state}`)
    const selectedSlotTimelineKeys = this.slotTimelineToggleTargets
      .filter((toggle) => toggle.checked)
      .map((toggle) => toggle.value)

    if (storedPlanStates.length > 0) {
      url.searchParams.set(this.constructor.planStatesParam, storedPlanStates.join(","))
    } else {
      url.searchParams.delete(this.constructor.planStatesParam)
    }
    url.searchParams.delete(this.constructor.selectedPlansParam)
    url.searchParams.delete(this.constructor.planModeParam)

    if (selectedSlotTimelineKeys.length > 0) {
      url.searchParams.set(this.constructor.slotTimelinesParam, selectedSlotTimelineKeys.join(","))
    } else {
      url.searchParams.delete(this.constructor.slotTimelinesParam)
    }

    if (this.hasPastToggleTarget && this.pastToggleTarget.checked) {
      url.searchParams.set(this.constructor.showPastParam, "1")
    } else {
      url.searchParams.delete(this.constructor.showPastParam)
    }

    if (this.hasExcludeArchivedPlansToggleTarget && !this.excludeArchivedPlansToggleTarget.checked) {
      url.searchParams.set(this.constructor.excludeArchivedPlansParam, "0")
    } else {
      url.searchParams.delete(this.constructor.excludeArchivedPlansParam)
    }

    if (this.hasUntimedSlotsToggleTarget && !this.untimedSlotsToggleTarget.checked) {
      url.searchParams.set(this.constructor.includeUntimedSlotsParam, "0")
    } else {
      url.searchParams.delete(this.constructor.includeUntimedSlotsParam)
    }

    url.searchParams.delete("people_include_untimed_jobs")

    const timelineSort = this.hasTimelineSortTarget ? this.timelineSortTarget.value : "hours"
    const timelineSortDirection = this.hasTimelineSortDirectionTarget ? this.timelineSortDirectionTarget.value : "desc"
    const timelineShowAllPeople = this.timelineShowAllPeopleEnabled()
    const peopleSearch = this.selectedPeopleSearch()
    const peoplePlanningFilters = this.serializedPeoplePlanningFilters()
    const peoplePlanningEffects = this.selectedPeoplePlanningEffects()
    const hasPeopleFilterBar = Boolean(this.element.querySelector("[data-people-filter-bar]"))
    const legacyTimelinePlanningFilter = this.selectedTimelinePlanningFilter()
    const preserveLegacyPlanningFilter = !hasPeopleFilterBar && Boolean(legacyTimelinePlanningFilter)
    const savedViewActive = this.hasCurrentViewValue && Boolean(this.currentViewValue)
    const groupingCompactionStrategy = this.hasGroupingCompactionStrategyTarget ? this.groupingCompactionStrategyTarget.value : "auto"

    if (timelineSort && timelineSort !== "hours") {
      url.searchParams.set(this.constructor.timelineSortParam, timelineSort)
    } else {
      url.searchParams.delete(this.constructor.timelineSortParam)
    }

    if (timelineSortDirection && timelineSortDirection !== "desc") {
      url.searchParams.set(this.constructor.timelineSortDirectionParam, timelineSortDirection)
    } else {
      url.searchParams.delete(this.constructor.timelineSortDirectionParam)
    }

    if (timelineShowAllPeople) {
      url.searchParams.set(this.constructor.timelineShowAllPeopleParam, "1")
    } else if (savedViewActive) {
      url.searchParams.set(this.constructor.timelineShowAllPeopleParam, "0")
    } else {
      url.searchParams.delete(this.constructor.timelineShowAllPeopleParam)
    }

    if (peopleSearch) {
      url.searchParams.set(this.constructor.peopleSearchParam, peopleSearch)
    } else if (savedViewActive) {
      url.searchParams.set(this.constructor.peopleSearchParam, "")
    } else {
      url.searchParams.delete(this.constructor.peopleSearchParam)
    }

    if (peoplePlanningFilters) {
      url.searchParams.set(this.constructor.peoplePlanningFiltersParam, peoplePlanningFilters)
    } else if (savedViewActive && !preserveLegacyPlanningFilter) {
      url.searchParams.set(this.constructor.peoplePlanningFiltersParam, "")
    } else {
      url.searchParams.delete(this.constructor.peoplePlanningFiltersParam)
    }

    if (peoplePlanningEffects.length > 0) {
      url.searchParams.set(this.constructor.peoplePlanningEffectsParam, peoplePlanningEffects.join(","))
    } else if (savedViewActive && !preserveLegacyPlanningFilter) {
      url.searchParams.set(this.constructor.peoplePlanningEffectsParam, "")
    } else {
      url.searchParams.delete(this.constructor.peoplePlanningEffectsParam)
    }

    if (hasPeopleFilterBar) {
      url.searchParams.delete(this.constructor.timelinePlanningFilterParam)
    } else {
      if (legacyTimelinePlanningFilter) {
        url.searchParams.set(this.constructor.timelinePlanningFilterParam, legacyTimelinePlanningFilter)
      } else if (savedViewActive) {
        url.searchParams.set(this.constructor.timelinePlanningFilterParam, "")
      } else {
        url.searchParams.delete(this.constructor.timelinePlanningFilterParam)
      }
    }

    if (this.hasGroupingCompactionStrategyTarget && groupingCompactionStrategy !== "auto") {
      url.searchParams.set(this.constructor.groupingCompactionStrategyParam, groupingCompactionStrategy)
    } else {
      url.searchParams.delete(this.constructor.groupingCompactionStrategyParam)
    }

    if (this.hasCurrentViewValue && this.currentViewValue) {
      url.searchParams.set("view", this.currentViewValue)
    } else {
      url.searchParams.delete("view")
    }

    window.history.replaceState({}, "", url)
    this.updateViewModeLinks(url)
    return url
  }

  updateViewModeLinks(currentUrl = new URL(window.location.href)) {
    this.viewModeLinkTargets.forEach((link) => {
      const viewMode = link.dataset.viewMode
      if (!viewMode) return

      const url = new URL(currentUrl.toString())
      url.searchParams.set(this.constructor.viewModeParam, viewMode)
      link.href = url.toString()
    })
  }

  shouldReloadForFilterChange(event) {
    if (!event?.currentTarget) return false

    const target = event.currentTarget
    if (this.hasPastToggleTarget && target === this.pastToggleTarget) return true

    if (this.currentViewModeValue !== "timeline") return false

    return this.slotTimelineToggleTargets.includes(target) ||
      this.planToggleTargets.includes(target) ||
      (this.hasAllPlansToggleTarget && target === this.allPlansToggleTarget) ||
      (this.hasExcludeArchivedPlansToggleTarget && target === this.excludeArchivedPlansToggleTarget) ||
      (this.hasUntimedSlotsToggleTarget && target === this.untimedSlotsToggleTarget) ||
      (this.hasTimelineShowAllPeopleTarget && target === this.timelineShowAllPeopleTarget) ||
      target.matches?.("[data-people-filter-bar]") ||
      Boolean(target.closest?.("[data-people-filter-bar]"))
  }

  reloadSchedule(url) {
    if (!url) return

    this.peopleFilterReloadStarted = true
    if (window.Turbo?.visit) {
      window.Turbo.visit(url.toString(), {action: "replace"})
    } else {
      window.location.assign(url.toString())
    }
  }

  loadPublicIdentity() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicIdentitySelectTarget || !this.hasPublicIdentityStorageKeyValue) return

    const currentValue = this.selectedPublicIdentityKey()
    if (currentValue) {
      this.savePublicIdentity()
      this.savePublicIdentityCookie()
      return
    }

    try {
      const storedValue = window.localStorage.getItem(this.publicIdentityStorageKeyValue)
      if (!storedValue) return

      if (this.publicIdentityOptionExists(storedValue)) {
        this.setPublicIdentitySelectValue(storedValue, {suppressChange: true})
        this.savePublicIdentityCookie()
      } else {
        window.localStorage.removeItem(this.publicIdentityStorageKeyValue)
      }
    } catch (_error) {
    }
  }

  loadPublicViewMode() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicViewModeButtonTarget) return

    const currentMode = this.selectedPublicViewMode()
    if (this.validPublicViewMode(currentMode)) {
      this.setPublicViewMode(currentMode)
      this.savePublicViewMode()
      this.savePublicViewModeCookie()
      return
    }

    let storedValue = null
    try {
      storedValue = this.hasPublicViewModeStorageKeyValue ?
        window.localStorage.getItem(this.publicViewModeStorageKeyValue) :
        null
    } catch (_error) {
    }

    this.setPublicViewMode(this.validPublicViewMode(storedValue) ? storedValue : "all")
    this.savePublicViewModeCookie()
  }

  loadPublicTextScale() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicTextScaleButtonTarget) return

    let storedValue = null
    try {
      storedValue = this.hasPublicTextScaleStorageKeyValue ?
        window.localStorage.getItem(this.publicTextScaleStorageKeyValue) :
        null
    } catch (_error) {
    }

    const migratedValue = this.migrateLegacyPublicTextScale(storedValue)
    this.setPublicTextScale(this.validPublicTextScale(migratedValue) ? migratedValue : this.constructor.defaultPublicTextScale)
  }

  savePublicIdentity() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicIdentityStorageKeyValue) return

    try {
      const selectedIdentityKey = this.selectedPublicIdentityKey()
      if (selectedIdentityKey) {
        window.localStorage.setItem(this.publicIdentityStorageKeyValue, selectedIdentityKey)
      } else {
        window.localStorage.removeItem(this.publicIdentityStorageKeyValue)
      }
    } catch (_error) {
    }
  }

  savePublicIdentityCookie() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicIdentityCookieNameValue) return

    this.writePublicCookie(this.publicIdentityCookieNameValue, this.selectedPublicIdentityKey())
  }

  savePublicViewMode() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicViewModeStorageKeyValue) return

    try {
      window.localStorage.setItem(this.publicViewModeStorageKeyValue, this.selectedPublicViewMode() || "all")
    } catch (_error) {
    }
  }

  savePublicViewModeCookie() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicViewModeCookieNameValue) return

    const selectedIdentityKey = this.selectedPublicIdentityKey()
    const publicViewMode = this.selectedPublicViewMode(selectedIdentityKey)
    const normalizedMode = (!selectedIdentityKey && publicViewMode === "all") ? "" : publicViewMode
    this.writePublicCookie(this.publicViewModeCookieNameValue, normalizedMode)
  }

  savePublicTextScale() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicTextScaleStorageKeyValue) return

    try {
      window.localStorage.setItem(this.publicTextScaleStorageKeyValue, this.selectedPublicTextScale())
    } catch (_error) {
    }
  }

  persistPublicSelectionToUrl() {
    if (!this.publicIdentityEnabledValue) return

    const url = new URL(window.location.href)
    url.searchParams.delete(this.constructor.publicIdentityParam)
    url.searchParams.delete(this.constructor.publicViewModeParam)
    url.searchParams.delete(this.constructor.legacyPublicOpenShiftsParam)

    window.history.replaceState({}, "", url)
  }

  writePublicCookie(name, value) {
    if (!name) return

    const baseCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value || "")}; path=/plans; SameSite=Lax`
    if (value) {
      document.cookie = `${baseCookie}; max-age=${60 * 60 * 24 * 365}`
    } else {
      document.cookie = `${baseCookie}; expires=Thu, 01 Jan 1970 00:00:00 GMT`
    }
  }

  selectedPublicIdentityKey() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicIdentitySelectTarget) return ""

    return this.publicIdentitySelectTarget.value || ""
  }

  publicIdentityOptionExists(value) {
    return Array.from(this.publicIdentitySelectTarget.options).some((option) => option.value === value)
  }

  setPublicIdentitySelectValue(value, {notify = true, suppressChange = false} = {}) {
    if (!this.hasPublicIdentitySelectTarget) return

    this.publicIdentitySelectTarget.value = value
    this.suppressNextPublicIdentityChange = suppressChange

    if (window.jQuery && window.jQuery(this.publicIdentitySelectTarget).data("select2")) {
      window.jQuery(this.publicIdentitySelectTarget).val(value).trigger(notify ? "change" : "change.select2")
      return
    }

    if (notify) {
      this.publicIdentitySelectTarget.dispatchEvent(new Event("change", {bubbles: true}))
    }
  }

  selectedPublicViewMode(selectedIdentityKey = this.selectedPublicIdentityKey()) {
    if (!this.publicIdentityEnabledValue || !this.hasPublicViewModeButtonTarget) return "all"

    const fallbackMode = selectedIdentityKey ? "mine_and_open" : "all"
    const selectedButton = this.publicViewModeButtonTargets.find((button) => button.dataset.selected === "true")
    const selectedMode = this.validPublicViewMode(this.currentPublicViewMode) ?
      this.currentPublicViewMode :
      (this.validPublicViewMode(selectedButton?.dataset.publicViewMode) ? selectedButton.dataset.publicViewMode : fallbackMode)

    if (!selectedIdentityKey && !this.publicViewModeAvailableWithoutIdentity(selectedMode)) {
      return "all"
    }

    return selectedMode
  }

  selectedPublicTextScale() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicTextScaleButtonTarget) return this.constructor.defaultPublicTextScale

    return this.validPublicTextScale(this.element.dataset.publicTextScale) ? this.element.dataset.publicTextScale : this.constructor.defaultPublicTextScale
  }

  setPublicTextScale(value, {announceChange = false} = {}) {
    if (!this.hasPublicTextScaleButtonTarget) return

    const scale = this.validPublicTextScale(value) ? value : this.constructor.defaultPublicTextScale
    this.element.dataset.publicTextScale = scale
    this.publicTextScaleButtonTargets.forEach((button) => {
      const direction = Number.parseInt(button.dataset.publicTextScaleDirection || "0", 10)
      const currentScale = Number.parseInt(scale, 10)
      button.disabled = direction < 0 ? currentScale <= 1 : currentScale >= 5
    })

    if (this.hasPublicTextScaleDotTarget) {
      this.publicTextScaleDotTargets.forEach((dot) => {
        const selected = dot.dataset.publicTextScaleStep === scale
        dot.dataset.selected = selected ? "true" : "false"
      })
    }

    if (announceChange) this.showPublicTextScaleFeedback()
  }

  validPublicTextScale(value) {
    return ["1", "2", "3", "4", "5"].includes(value)
  }

  migrateLegacyPublicTextScale(value) {
    switch (value) {
      case "small":
        return "2"
      case "medium":
        return "3"
      case "large":
        return "4"
      default:
        return value
    }
  }

  renderPublicTextScale() {
    if (!this.publicIdentityEnabledValue || !this.hasPublicTextScaleButtonTarget) return

    this.setPublicTextScale(this.selectedPublicTextScale())
  }

  showPublicTextScaleFeedback() {
    if (!this.hasPublicTextScaleGroupTarget) return

    this.publicTextScaleGroupTarget.dataset.feedbackActive = "true"
    if (this.publicTextScaleFeedbackTimeout) {
      window.clearTimeout(this.publicTextScaleFeedbackTimeout)
    }

    this.publicTextScaleFeedbackTimeout = window.setTimeout(() => {
      if (this.hasPublicTextScaleGroupTarget) delete this.publicTextScaleGroupTarget.dataset.feedbackActive
      this.publicTextScaleFeedbackTimeout = null
    }, 1500)
  }

  clearPublicTextScaleFeedback() {
    if (this.publicTextScaleFeedbackTimeout) {
      window.clearTimeout(this.publicTextScaleFeedbackTimeout)
      this.publicTextScaleFeedbackTimeout = null
    }

    if (this.hasPublicTextScaleGroupTarget) delete this.publicTextScaleGroupTarget.dataset.feedbackActive
  }

  renderPublicIdentity(selectedIdentityKey, publicViewMode) {
    if (!this.publicIdentityEnabledValue) return

    const selectedOption = this.hasPublicIdentitySelectTarget ?
      this.publicIdentitySelectTarget.selectedOptions?.[0] :
      null
    const hasSelection = Boolean(selectedIdentityKey && selectedOption)

    if (this.hasPublicViewModeGroupTarget) {
      this.renderPublicViewModeControls(publicViewMode, hasSelection)
    }

    if (this.hasPublicIdentitySummaryTarget) {
      const showSummary = hasSelection || publicViewMode === "only_open"
      this.publicIdentitySummaryTarget.classList.toggle("hidden", !showSummary)
      if (showSummary) {
        const translationKey = {
          only_mine: "public_identity.selected_only_mine",
          only_open: "public_identity.selected_only_open",
          mine_and_open: "public_identity.selected_mine_and_open",
          all: "public_identity.selected_all",
        }[publicViewMode] || "public_identity.selected_mine_and_open"
        const selectedName = selectedOption?.textContent?.trim() || ""
        this.publicIdentitySummaryTarget.textContent = this.translationFor(translationKey, "Showing your personal assignments for %{name}.")
          .replace("%{name}", selectedName)
      }
    }
  }

  renderPublicCalendarLinks(selectedIdentityKey) {
    if (!this.publicIdentityEnabledValue || !this.hasPublicCalendarActionsTarget) return

    const hasSelection = Boolean(selectedIdentityKey)
    this.publicCalendarActionsTargets.forEach((actions) => {
      const liveUrl = hasSelection ? this.publicCalendarUrlWithIdentity(actions.dataset.publicCalendarUrlBase, selectedIdentityKey) : ""
      const webcalUrl = liveUrl ? this.webcalUrl(liveUrl) : ""
      const calendarName = this.publicCalendarName(actions, selectedIdentityKey)
      const device = this.publicCalendarDevice()

      actions.dataset.publicCalendarLiveUrl = liveUrl
      actions.dataset.publicCalendarName = calendarName
      actions.dataset.publicCalendarDevice = device
      actions.classList.toggle("hidden", !hasSelection)
      actions.classList.toggle("flex", hasSelection)

      actions.querySelectorAll("[data-public-calendar-apple-link]").forEach((link) => {
        const showAppleLink = hasSelection && this.publicCalendarAppleLinkAvailable(device)
        link.href = webcalUrl || "#"
        link.classList.toggle("hidden", !showAppleLink)
        link.classList.toggle("inline-flex", showAppleLink)
        link.setAttribute("aria-disabled", hasSelection ? "false" : "true")
      })

      actions.querySelectorAll("[data-public-calendar-google-link]").forEach((link) => {
        link.href = liveUrl ? this.googleCalendarUrl(liveUrl) : "#"
        link.dataset.publicCalendarLiveUrl = liveUrl
        link.setAttribute("aria-disabled", hasSelection ? "false" : "true")
      })

      actions.querySelectorAll("[data-public-calendar-outlook-link]").forEach((link) => {
        link.href = liveUrl ? this.outlookCalendarUrl(liveUrl, calendarName) : "#"
        link.dataset.publicCalendarLiveUrl = liveUrl
        link.setAttribute("aria-disabled", hasSelection ? "false" : "true")
      })

      actions.querySelectorAll("[data-public-calendar-copy-button]").forEach((button) => {
        button.disabled = !hasSelection
        button.dataset.shareUrl = liveUrl
      })
    })

    if (this.hasPublicCalendarLauncherTarget) {
      this.publicCalendarLauncherTargets.forEach((launcher) => {
        launcher.classList.toggle("hidden", !hasSelection)
        launcher.classList.toggle("inline-flex", hasSelection)
      })
    }
  }

  schedulePublicCalendarSuggestion(selectedIdentityKey) {
    if (!this.hasPublicCalendarSuggestionTarget) return

    if (!this.publicCalendarSuggestionEligible(selectedIdentityKey)) {
      this.hidePublicCalendarSuggestion()
      this.clearPublicCalendarSuggestionTimer()
      this.publicCalendarSuggestionIdentityKey = null
      return
    }

    if (this.publicCalendarSuggestionIdentityKey !== selectedIdentityKey) {
      this.hidePublicCalendarSuggestion()
      this.publicCalendarSuggestionIdentityKey = selectedIdentityKey
      this.publicCalendarSuggestionStartedAt = Date.now()
      this.publicCalendarSuggestionScrolled = this.publicCalendarSuggestionScrollSatisfied()
    }

    if (this.publicCalendarSuggestionMuted(selectedIdentityKey)) {
      this.clearPublicCalendarSuggestionTimer()
      if (!this.publicCalendarSuggestionVisible) this.hidePublicCalendarSuggestion()
      return
    }

    if (this.publicCalendarSuggestionVisible) return

    this.clearPublicCalendarSuggestionTimer()
    const remainingDelay = Math.max(0, this.constructor.publicCalendarSuggestionDelayMs - (Date.now() - this.publicCalendarSuggestionStartedAt))
    this.publicCalendarSuggestionTimer = window.setTimeout(() => this.maybeShowPublicCalendarSuggestion(), remainingDelay)
  }

  handlePublicCalendarSuggestionScroll() {
    if (this.publicCalendarSuggestionScrolled || !this.hasPublicCalendarSuggestionTarget) return

    if (this.publicCalendarSuggestionScrollSatisfied()) {
      this.publicCalendarSuggestionScrolled = true
      this.maybeShowPublicCalendarSuggestion()
    }
  }

  maybeShowPublicCalendarSuggestion() {
    this.clearPublicCalendarSuggestionTimer()

    const selectedIdentityKey = this.selectedPublicIdentityKey()
    if (
      !this.publicCalendarSuggestionEligible(selectedIdentityKey) ||
      this.publicCalendarSuggestionMuted(selectedIdentityKey) ||
      !this.publicCalendarSuggestionScrolled ||
      Date.now() - this.publicCalendarSuggestionStartedAt < this.constructor.publicCalendarSuggestionDelayMs
    ) return

    this.showPublicCalendarSuggestion()
  }

  showPublicCalendarSuggestion({expanded = false} = {}) {
    if (!this.hasPublicCalendarSuggestionTarget) return

    const suggestion = this.publicCalendarSuggestionTarget
    if (expanded) {
      this.expandPublicCalendarSuggestionPanel()
    } else if (!this.publicCalendarSuggestionVisible) {
      this.collapsePublicCalendarSuggestion()
    }
    if (this.publicCalendarSuggestionVisible) {
      suggestion.classList.remove("hidden")
      suggestion.setAttribute("aria-hidden", "false")
      suggestion.dataset.visible = "true"
      return
    }
    suggestion.classList.remove("hidden")
    suggestion.setAttribute("aria-hidden", "false")
    this.publicCalendarSuggestionVisible = true

    window.requestAnimationFrame(() => {
      if (this.hasPublicCalendarSuggestionTarget) {
        this.publicCalendarSuggestionTarget.dataset.visible = "true"
      }
    })
  }

  hidePublicCalendarSuggestion() {
    if (!this.hasPublicCalendarSuggestionTarget) return

    const suggestion = this.publicCalendarSuggestionTarget
    suggestion.dataset.visible = "false"
    suggestion.setAttribute("aria-hidden", "true")
    this.publicCalendarSuggestionVisible = false

    window.setTimeout(() => {
      if (suggestion.dataset.visible !== "true") suggestion.classList.add("hidden")
    }, 180)
  }

  dismissPublicCalendarSuggestion(event) {
    event?.preventDefault()
    this.mutePublicCalendarSuggestion()
  }

  openPublicCalendarLinks(event) {
    event?.preventDefault()
    const selectedIdentityKey = this.selectedPublicIdentityKey()
    if (!this.publicCalendarSuggestionEligible(selectedIdentityKey)) return

    this.clearPublicCalendarSuggestionTimer()
    this.publicCalendarSuggestionIdentityKey = selectedIdentityKey
    this.publicCalendarSuggestionStartedAt = Date.now()
    this.publicCalendarSuggestionScrolled = true
    this.showPublicCalendarSuggestion({expanded: true})
  }

  expandPublicCalendarSuggestion(event) {
    event?.preventDefault()
    this.expandPublicCalendarSuggestionPanel()
  }

  expandPublicCalendarSuggestionPanel() {
    const panel = this.publicCalendarSuggestionPanel()
    if (panel) panel.dataset.expanded = "true"
  }

  collapsePublicCalendarSuggestion() {
    const panel = this.publicCalendarSuggestionPanel()
    if (panel) panel.dataset.expanded = "false"
  }

  publicCalendarSuggestionPanel() {
    return this.hasPublicCalendarSuggestionTarget ?
      this.publicCalendarSuggestionTarget.querySelector(".public-calendar-suggestion-panel") :
      null
  }

  mutePublicCalendarSuggestion() {
    this.persistPublicCalendarSuggestionMuted()
    this.hidePublicCalendarSuggestion()
    this.clearPublicCalendarSuggestionTimer()
  }

  publicCalendarSuggestionEligible(selectedIdentityKey) {
    return this.publicIdentityEnabledValue &&
      Boolean(selectedIdentityKey) &&
      this.hasPublicCalendarActionsTarget &&
      this.publicCalendarActionsTargets.some((actions) => actions.dataset.publicCalendarLiveUrl)
  }

  publicCalendarSuggestionScrollSatisfied() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0
    const scrollableDistance = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight
    if (scrollableDistance <= this.constructor.publicCalendarSuggestionScrollThreshold) return true

    return scrollTop >= this.constructor.publicCalendarSuggestionScrollThreshold
  }

  publicCalendarSuggestionMuted(selectedIdentityKey = this.selectedPublicIdentityKey()) {
    const storageKey = this.publicCalendarSuggestionStorageKey(selectedIdentityKey)
    if (!storageKey) return false

    try {
      const storedValue = window.localStorage.getItem(storageKey)
      if (!storedValue) return false

      const parsedValue = JSON.parse(storedValue)
      const mutedUntil = Date.parse(parsedValue?.mutedUntil)
      if (!mutedUntil || mutedUntil <= Date.now()) {
        this.clearPublicCalendarSuggestionMute(storageKey)
        return false
      }

      return true
    } catch (_error) {
      this.clearPublicCalendarSuggestionMute(storageKey)
      return false
    }
  }

  persistPublicCalendarSuggestionMuted(selectedIdentityKey = this.selectedPublicIdentityKey()) {
    const storageKey = this.publicCalendarSuggestionStorageKey(selectedIdentityKey)
    if (!storageKey) return

    try {
      const mutedUntil = new Date(Date.now() + this.constructor.publicCalendarSuggestionMuteMs).toISOString()
      window.localStorage.setItem(storageKey, JSON.stringify({mutedUntil}))
    } catch (_error) {
    }
  }

  clearPublicCalendarSuggestionMute(storageKey) {
    try {
      window.localStorage.removeItem(storageKey)
    } catch (_error) {
    }
  }

  publicCalendarSuggestionStorageKey(selectedIdentityKey = this.selectedPublicIdentityKey()) {
    if (!selectedIdentityKey || !this.hasPublicCalendarActionsTarget) return ""

    const baseUrl = this.publicCalendarActionsTargets[0]?.dataset.publicCalendarUrlBase || window.location.pathname
    return `staffing-public-calendar-suggestion-muted:${baseUrl}:${selectedIdentityKey}`
  }

  clearPublicCalendarSuggestionTimer() {
    if (this.publicCalendarSuggestionTimer) {
      window.clearTimeout(this.publicCalendarSuggestionTimer)
      this.publicCalendarSuggestionTimer = null
    }
  }

  publicCalendarUrlWithIdentity(baseUrl, selectedIdentityKey) {
    if (!baseUrl || !selectedIdentityKey) return ""

    const url = new URL(baseUrl, window.location.href)
    url.searchParams.set(this.constructor.publicIdentityParam, selectedIdentityKey)
    return url.toString()
  }

  publicCalendarName(actions, selectedIdentityKey) {
    const baseName = actions.dataset.publicCalendarBaseName || actions.dataset.publicCalendarName || ""
    const selectedOption = this.hasPublicIdentitySelectTarget ? this.publicIdentitySelectTarget.selectedOptions?.[0] : null
    const selectedName = selectedIdentityKey && selectedOption ? selectedOption.textContent.trim() : ""

    return [baseName, selectedName].filter(Boolean).join(" - ")
  }

  webcalUrl(liveUrl) {
    if (!liveUrl) return "#"

    return new URL(liveUrl, window.location.href).toString().replace(/^https?:/i, "webcal:")
  }

  googleCalendarUrl(liveUrl) {
    const url = new URL("https://calendar.google.com/calendar/r")
    url.searchParams.set("cid", liveUrl)
    return url.toString()
  }

  outlookCalendarUrl(liveUrl, calendarName) {
    const url = new URL("https://outlook.office.com/calendar/addcalendar")
    url.searchParams.set("url", liveUrl)
    if (calendarName) url.searchParams.set("name", calendarName)
    return url.toString()
  }

  publicCalendarDevice() {
    const userAgent = navigator.userAgent || ""
    const platform = navigator.userAgentData?.platform || navigator.platform || ""
    const appleTouchDesktop = platform === "MacIntel" && navigator.maxTouchPoints > 1
    const apple = appleTouchDesktop || /iPhone|iPad|iPod|Mac/i.test(platform) || /iPhone|iPad|iPod|Mac OS X/i.test(userAgent)
    const android = /Android/i.test(userAgent)
    const mobile = appleTouchDesktop || android || /Mobi|iPhone|iPad|iPod/i.test(userAgent)

    if (apple && mobile) return "apple_mobile"
    if (apple) return "apple_desktop"
    if (android) return "android"
    if (mobile) return "mobile"
    return "desktop"
  }

  publicCalendarAppleLinkAvailable(device) {
    return device === "apple_mobile" || device === "apple_desktop"
  }

  renderHighlightedPeople(selectedIdentityKey) {
    const linkedPeople = this.publicDomIndex?.linkedPeople || this.element.querySelectorAll("[data-people-canvas-linked-person]")
    linkedPeople.forEach((element) => {
      const highlighted = Boolean(selectedIdentityKey) && element.dataset.personIdentityKey === selectedIdentityKey

      element.dataset.selfHighlighted = highlighted ? "true" : "false"
      element.classList.toggle("text-sky-900", highlighted)
      element.classList.toggle("ring-sky-300", highlighted)
      element.classList.toggle("dark:text-sky-100", highlighted)
      element.classList.toggle("dark:ring-sky-400/60", highlighted)
    })
  }

  renderPublicPlanHighlights(selectedIdentityKey) {
    const publicPlanElements = this.publicDomIndex?.publicPlanElements || this.element.querySelectorAll("[data-public-plan-identity-keys]")
    const changedElements = new WeakSet()
    publicPlanElements.forEach((element) => {
      const highlighted = Boolean(selectedIdentityKey) && this.publicPlanIdentityKeys(element).includes(selectedIdentityKey)
      const highlightedValue = highlighted ? "true" : "false"
      if (element.dataset.publicPlanHighlighted === highlightedValue) return

      element.dataset.publicPlanHighlighted = highlightedValue
      changedElements.add(element)

      if (element.matches("[data-people-canvas-card]")) {
        element.classList.toggle("ring-sky-200", highlighted)
        element.classList.toggle("bg-sky-50/80", highlighted)
        element.classList.toggle("dark:bg-sky-900/20", highlighted)
        element.classList.toggle("dark:ring-sky-500/40", highlighted)
      }
    })

    const publicPlanBadges = this.publicDomIndex?.publicPlanBadges || this.element.querySelectorAll("[data-public-plan-badge]")
    publicPlanBadges.forEach((badge) => {
      const container = badge.closest("[data-public-plan-identity-keys]")
      if (!container || !changedElements.has(container)) return
      const highlighted = container?.dataset.publicPlanHighlighted === "true"
      const defaultClasses = [
        "bg-slate-100",
        "text-slate-600",
        "dark:bg-slate-700/70",
        "dark:text-slate-200",
      ]
      const highlightedClasses = [
        "bg-sky-100",
        "text-slate-700",
        "dark:bg-sky-900/30",
        "dark:text-sky-100",
      ]

      defaultClasses.forEach((className) => badge.classList.toggle(className, !highlighted))
      highlightedClasses.forEach((className) => badge.classList.toggle(className, highlighted))
    })
  }

  publicPlanIdentityKeys(element) {
    return (element.dataset.publicPlanIdentityKeys || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
  }

  renderPublicParticipationButtons(selectedIdentityKey, publicViewMode, rows = this.assignmentRows) {
    rows.forEach((row) => {
      const rowActivePersonIds = this.rowActivePersonIds(row)
      const rowPendingUnregistrationIdentityKeys = this.rowPendingUnregistrationIdentityKeys(row)
      const rowSignupPreviewProblemIdentityKeys = this.rowSignupPreviewProblemIdentityKeys(row)
      const rowSignupPreviewHintIdentityKeys = this.rowSignupPreviewHintIdentityKeys(row)
      const rowCurrentOverlapIdentityKeys = this.rowCurrentOverlapIdentityKeys(row)
      const canSignUp = this.rowJoinEligible(row, selectedIdentityKey)
      const selectedPersonIsInvited = Boolean(selectedIdentityKey) &&
        this.rowInvitedIdentityKeys(row).includes(selectedIdentityKey)
      const selectedPersonIsRemoved = Boolean(selectedIdentityKey) &&
        this.rowRemovedIdentityKeys(row).includes(selectedIdentityKey)
      const canResetUnregistration = Boolean(selectedIdentityKey) &&
        rowPendingUnregistrationIdentityKeys.includes(selectedIdentityKey)
      const canRequestUnregistration = Boolean(selectedIdentityKey) &&
        rowActivePersonIds.includes(selectedIdentityKey) &&
        !rowPendingUnregistrationIdentityKeys.includes(selectedIdentityKey)
      const showSignupPreviewProblem = canSignUp &&
        Boolean(selectedIdentityKey) &&
        rowSignupPreviewProblemIdentityKeys.includes(selectedIdentityKey)
      const showSignupPreviewHint = !showSignupPreviewProblem &&
        canSignUp &&
        Boolean(selectedIdentityKey) &&
        rowSignupPreviewHintIdentityKeys.includes(selectedIdentityKey)
      const showSignupWarning = showSignupPreviewProblem || showSignupPreviewHint
      const showCurrentOverlap = Boolean(selectedIdentityKey) &&
        rowCurrentOverlapIdentityKeys.includes(selectedIdentityKey)
      const potentiallyOverlappingForSelectedIdentity = showCurrentOverlap || showSignupPreviewProblem || showSignupPreviewHint
      const selectedPersonIsAssigned = Boolean(selectedIdentityKey) && rowActivePersonIds.includes(selectedIdentityKey)
      const selectedPersonPendingUnregistration = selectedPersonIsAssigned &&
        rowPendingUnregistrationIdentityKeys.includes(selectedIdentityKey)
      const publicSignupRowStateEnabled = row.dataset.publicSignupRowStateEnabled === "true"
      const showSignupCapacityStatus = canSignUp && !showSignupWarning

      row.classList.toggle("public-signup-row--selected", publicSignupRowStateEnabled && selectedPersonIsAssigned && !selectedPersonPendingUnregistration)
      row.classList.toggle("public-signup-row--invited", publicSignupRowStateEnabled && selectedPersonIsInvited && !selectedPersonIsAssigned)
      row.classList.toggle("public-signup-row--removed", publicSignupRowStateEnabled && selectedPersonIsRemoved && !selectedPersonIsAssigned && !selectedPersonIsInvited)

      row.querySelectorAll("[data-people-canvas-public-identity-input]").forEach((input) => {
        input.value = selectedIdentityKey || ""
      })

      row.querySelectorAll("[data-people-canvas-public-view-mode-input]").forEach((input) => {
        input.value = publicViewMode
      })

      row.querySelectorAll("[data-public-signup-control]").forEach((control) => {
        control.classList.toggle("hidden", !canSignUp)
        control.classList.toggle("flex", canSignUp)
      })

      row.querySelectorAll("[data-public-signup-control-spacer]").forEach((spacer) => {
        spacer.classList.toggle("hidden", !canSignUp)
        spacer.classList.toggle("inline-flex", canSignUp)
      })

      row.querySelectorAll("[data-people-canvas-public-signup-button]").forEach((button) => {
        this.setPublicParticipationButtonDisabled(button, !canSignUp)
        const warningTitle = showSignupPreviewProblem ? button.dataset.publicSignupProblemTitle : button.dataset.publicSignupHintTitle

        button.title = showSignupWarning ? warningTitle : button.dataset.publicSignupDefaultTitle
        button.classList.toggle("public-invitation-accept", selectedPersonIsInvited && !showSignupWarning)
        this.swapButtonClasses(
          button,
          button.dataset.publicSignupDefaultClasses,
          button.dataset.publicSignupWarningClasses,
          showSignupWarning
        )

        button.querySelectorAll("[data-public-signup-action-label]").forEach((label) => {
          const defaultLabel = selectedPersonIsInvited ?
            button.dataset.publicSignupInviteActionLabel :
            button.dataset.publicSignupDefaultActionLabel
          const warningLabel = selectedPersonIsInvited ?
            button.dataset.publicSignupWarningInviteActionLabel :
            button.dataset.publicSignupWarningActionLabel
          this.setTextIfChanged(label, showSignupWarning ? warningLabel : defaultLabel)
        })
        button.querySelectorAll("[data-public-signup-action-icon]").forEach((icon) => {
          icon.classList.toggle("fa-thumbs-up", selectedPersonIsInvited)
          icon.classList.toggle("fa-plus", !selectedPersonIsInvited)
          icon.classList.toggle("fa-solid", selectedPersonIsInvited)
          icon.classList.toggle("fa-light", !selectedPersonIsInvited)
        })
      })

      row.querySelectorAll("[data-public-signup-state-button]").forEach((button) => {
        button.classList.toggle("hidden", !showSignupWarning)
        button.classList.toggle("inline-flex", showSignupWarning)
        button.title = showSignupWarning ? (showSignupPreviewProblem ? row.querySelector("[data-people-canvas-public-signup-button]")?.dataset.publicSignupProblemTitle : row.querySelector("[data-people-canvas-public-signup-button]")?.dataset.publicSignupHintTitle) || "" : ""
        button.querySelectorAll("[data-public-state-label-text]").forEach((label) => {
          this.setTextIfChanged(label, button.dataset.publicStateLabel || "")
        })
      })

      row.querySelectorAll("[data-public-decline-invite-button]").forEach((button) => {
        const canDecline = selectedPersonIsInvited && canSignUp
        button.classList.toggle("hidden", !canDecline)
        this.setPublicParticipationButtonDisabled(button, !canDecline)
      })

      row.querySelectorAll("[data-public-invite-status]").forEach((status) => {
        const showInviteStatus = selectedPersonIsInvited && canSignUp
        status.classList.toggle("hidden", !showInviteStatus)
        status.classList.toggle("inline-flex", showInviteStatus)
      })

      row.querySelectorAll("[data-public-signup-capacity-status]").forEach((status) => {
        status.classList.toggle("hidden", !showSignupCapacityStatus)
        status.classList.toggle("inline-flex", showSignupCapacityStatus)
      })

      row.querySelectorAll("[data-public-inline-capacity-status]").forEach((status) => {
        status.classList.toggle("hidden", showSignupCapacityStatus)
        status.classList.toggle("inline-flex", !showSignupCapacityStatus)
      })

      row.dataset.publicSignupPreviewProblem = showSignupPreviewProblem ? "true" : "false"
      row.dataset.publicSignupPreviewHint = showSignupPreviewHint ? "true" : "false"
      row.dataset.publicOverlapHiddenCandidate = potentiallyOverlappingForSelectedIdentity ? "true" : "false"

      row.querySelectorAll("[data-people-canvas-public-unregistration-request-button]").forEach((button) => {
        this.setPublicParticipationButtonDisabled(button, !canRequestUnregistration)
        this.swapButtonClasses(
          button,
          button.dataset.publicUnregistrationDefaultClasses,
          button.dataset.publicUnregistrationWarningClasses,
          false
        )
      })

      row.querySelectorAll("[data-people-canvas-public-unregistration-reset-button]").forEach((button) => {
        this.setPublicParticipationButtonDisabled(button, !canResetUnregistration)
        this.swapButtonClasses(
          button,
          button.dataset.publicUnregistrationDefaultClasses,
          button.dataset.publicUnregistrationWarningClasses,
          showCurrentOverlap
        )

        button.querySelectorAll("[data-public-unregistration-reset-action-label]").forEach((label) => {
          this.setTextIfChanged(label, showCurrentOverlap ? button.dataset.publicUnregistrationWarningActionLabel : button.dataset.publicUnregistrationDefaultActionLabel)
        })
      })

      row.querySelectorAll("[data-public-unregistration-request-control]").forEach((control) => {
        control.classList.toggle("hidden", !canRequestUnregistration)
        control.classList.toggle("flex", canRequestUnregistration)
      })

      row.querySelectorAll("[data-public-unregistration-request-control-spacer]").forEach((spacer) => {
        spacer.classList.toggle("hidden", !canRequestUnregistration)
        spacer.classList.toggle("inline-flex", canRequestUnregistration)
      })

      row.querySelectorAll("[data-public-unregistration-reset-control]").forEach((control) => {
        control.classList.toggle("hidden", !canResetUnregistration)
        control.classList.toggle("flex", canResetUnregistration)
      })

      row.querySelectorAll("[data-public-unregistration-reset-control-spacer]").forEach((spacer) => {
        spacer.classList.toggle("hidden", !canResetUnregistration)
        spacer.classList.toggle("inline-flex", canResetUnregistration)
      })

      row.querySelectorAll("[data-public-unregistration-state-button]").forEach((button) => {
        button.classList.toggle("hidden", !showCurrentOverlap)
        button.classList.toggle("inline-flex", showCurrentOverlap)
        button.querySelectorAll("[data-public-state-label-text]").forEach((label) => {
          this.setTextIfChanged(label, button.dataset.publicStateLabel || "")
        })
      })

      row.querySelectorAll("[data-public-signup-status]").forEach((status) => {
        status.classList.toggle("hidden", !canRequestUnregistration)
        status.classList.toggle("inline-flex", canRequestUnregistration)
      })

      row.querySelectorAll("[data-public-unregistration-pending-status]").forEach((status) => {
        status.classList.toggle("hidden", !canResetUnregistration)
        status.classList.toggle("inline-flex", canResetUnregistration)
      })

      row.querySelectorAll("[data-inline-button-group]").forEach((group) => {
        this.applyInlineButtonGroup(group)
      })
    })
  }

  renderPublicCommitments(selectedIdentityKey, filters, {sections = null} = {}) {
    if (!this.publicIdentityEnabledValue) return

    const showTotal = Boolean(selectedIdentityKey)
    let totalMinimumSeconds = 0
    let totalMaximumSeconds = 0
    let totalJobs = 0
    let totalConflicts = 0
    const sectionCommitmentRanges = new Map(this.sections.map((section) => [section, {
      minimum: 0,
      maximum: 0,
    }]))

    this.assignmentRows.forEach((row) => {
      if (!this.rowCountsForCommitment(row, selectedIdentityKey, filters)) return

      const section = row.closest("[data-people-canvas-filters-target~='section']")
      const sectionSeconds = sectionCommitmentRanges.get(section)
      const rowSeconds = this.rowCommitmentSecondsRange(row)

      if (sectionSeconds) {
        sectionSeconds.minimum += rowSeconds.minimum
        sectionSeconds.maximum += rowSeconds.maximum
      }
      totalMinimumSeconds += rowSeconds.minimum
      totalMaximumSeconds += rowSeconds.maximum
      totalJobs += 1
      if (this.rowCurrentOverlapIdentityKeys(row).includes(selectedIdentityKey)) {
        totalConflicts += 1
      }
    })

    this.sections.forEach((section) => {
      if (sections && !sections.has(section)) return
      this.renderPublicCommitmentElement(
        section.querySelector("[data-public-day-commitment]"),
        sectionCommitmentRanges.get(section) || {minimum: 0, maximum: 0},
        {
          show: showTotal,
          translationKey: "public_commitment.day",
          fallback: "%{hours} h",
        }
      )
    })

    this.renderPublicCommitmentElement(
      this.element.querySelector("[data-public-commitment-total]"),
      {
        minimum: totalMinimumSeconds,
        maximum: totalMaximumSeconds,
      },
      {
        show: showTotal,
        translationKey: "public_commitment.total",
        fallback: "%{hours} h",
        jobs: totalJobs,
        conflicts: totalConflicts,
      }
    )
  }

  rowCountsForCommitment(row, selectedIdentityKey, filters) {
    if (!selectedIdentityKey) return false
    if (!this.rowActivePersonIds(row).includes(selectedIdentityKey)) return false
    if (this.rowPendingUnregistrationIdentityKeys(row).includes(selectedIdentityKey)) return false

    return this.rowMatchesSharedFilters(row, filters)
  }

  renderPublicCommitmentElement(element, secondsRange, { show, translationKey, fallback, jobs = null, conflicts = null }) {
    if (!element) return

    const label = this.translationFor(translationKey, fallback)
      .replace("%{hours}", this.formatCommitmentHoursRange(secondsRange))
      .replace("__hours__", this.formatCommitmentHoursRange(secondsRange))
    element.querySelectorAll("[data-public-commitment-total-label], [data-public-day-commitment-label]").forEach((labelElement) => {
      this.setTextIfChanged(labelElement, label)
    })
    if (jobs !== null) {
      element.querySelectorAll("[data-public-commitment-jobs-label]").forEach((labelElement) => {
        this.setTextIfChanged(labelElement, this.formatPublicCommitmentCount(
          jobs,
          "public_commitment.jobs_one",
          "public_commitment.jobs_other",
          "%{count}",
          "%{count}"
        ))
      })
    }

    if (conflicts !== null) {
      element.querySelectorAll("[data-public-commitment-conflicts]").forEach((conflictsElement) => {
        conflictsElement.classList.toggle("hidden", conflicts <= 0)
        conflictsElement.classList.toggle("inline-flex", conflicts > 0)
      })
      element.querySelectorAll("[data-public-commitment-conflicts-label]").forEach((labelElement) => {
        this.setTextIfChanged(labelElement, this.formatPublicCommitmentCount(
          conflicts,
          "public_commitment.conflicts_one",
          "public_commitment.conflicts_other",
          "%{count} conflict",
          "%{count} conflicts"
        ))
      })
    }

    const empty = jobs === null ? (secondsRange.maximum || 0) <= 0 : jobs <= 0
    element.dataset.publicCommitmentEmpty = empty ? "true" : "false"
    const summary = element.closest("[data-public-commitment-summary]")
    if (summary) summary.dataset.publicCommitmentEmpty = empty ? "true" : "false"
    element.classList.toggle("invisible", !show)
    element.setAttribute("aria-hidden", show ? "false" : "true")
  }

  formatPublicCommitmentCount(count, singularKey, pluralKey, singularFallback, pluralFallback) {
    const translation = this.translationFor(count === 1 ? singularKey : pluralKey, count === 1 ? singularFallback : pluralFallback)
    return translation
      .replace("%{count}", count)
      .replace("__count__", count)
  }

  formatCommitmentHours(seconds) {
    const hours = Math.max(0, seconds) / 3600
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      maximumFractionDigits: 2,
    }).format(hours)
  }

  formatCommitmentHoursRange({ minimum, maximum }) {
    const minimumHours = this.formatCommitmentHours(minimum)
    const maximumHours = this.formatCommitmentHours(maximum)

    return minimumHours === maximumHours ? minimumHours : `${minimumHours}-${maximumHours}`
  }

  renderPublicOverlapCollapsedGroups(selectedIdentityKey, publicViewMode, {preserveExpanded = false} = {}) {
    if (preserveExpanded && this.publicSlotsQuerySelectorAll("[data-public-overlap-cluster-expanded='true']").length > 0) return

    this.resetPublicOverlapCollapsedGroups()
    if (!this.publicOverlapCollapsingEnabled(selectedIdentityKey, publicViewMode)) return

    this.sections.forEach((section) => {
      if (section.dataset.publicTimeFirstSection === "true" && !section.classList.contains("hidden")) {
        this.collapsePotentiallyOverlappingCardsInSection(section, selectedIdentityKey, publicViewMode)
      }
    })

    this.cards.forEach((card) => {
      if (this.scopeFor(card) !== "slots" || card.classList.contains("hidden")) return
      if (card.closest("[data-public-time-first-section='true']")) return
      this.collapsePotentiallyOverlappingGroupsInCard(card, selectedIdentityKey, publicViewMode)
    })
  }

  resetPublicOverlapCollapsedGroups() {
    this.publicSlotsQuerySelectorAll("[data-public-overlap-cluster]").forEach((cluster) => {
      const parent = cluster.parentNode
      if (!parent) return

      Array.from(cluster.children).forEach((child) => {
        if (child.matches("[data-public-overlap-collapse-summary]")) {
          child.remove()
        } else if (child.__publicOverlapPlaceholder?.parentNode) {
          child.__publicOverlapPlaceholder.replaceWith(child)
          delete child.__publicOverlapPlaceholder
        } else {
          parent.insertBefore(child, cluster)
        }
      })

      cluster.remove()
    })
    this.publicSlotsQuerySelectorAll("[data-public-overlap-collapse-summary]").forEach((summary) => summary.remove())
    this.publicSlotsQuerySelectorAll("[data-public-overlap-collapsed-card='true']").forEach((card) => {
      if (this.visibleRowsWithin(card).length > 0) {
        card.classList.remove("hidden")
      }
      delete card.dataset.publicOverlapCollapsedCard
    })
    this.publicSlotsQuerySelectorAll("[data-public-overlap-collapsed-group='true']").forEach((group) => {
      if (this.visibleRowsWithin(group).length > 0) {
        group.classList.remove("hidden")
      }
      delete group.dataset.publicOverlapCollapsedGroup
    })
  }

  collapsePotentiallyOverlappingCardsInSection(section, selectedIdentityKey, publicViewMode) {
    const cards = Array.from(section.querySelectorAll("[data-people-canvas-card]"))
    let run = []

    const flush = () => {
      if (run.length === 0) return
      this.insertPublicOverlapCollapseSummary(run, "publicOverlapCollapsedCard")
      run = []
    }

    cards.forEach((card) => {
      const row = card.querySelector("[data-people-canvas-assignment-row]")
      const visible = row?.dataset.filterVisible === "true" && !card.classList.contains("hidden")

      if (!visible) {
        return
      }

      if (this.rowPotentiallyOverlappingForSelectedIdentity(row, selectedIdentityKey, publicViewMode)) {
        run.push(card)
      } else {
        flush()
      }
    })

    flush()
  }

  collapsePotentiallyOverlappingGroupsInCard(card, selectedIdentityKey, publicViewMode) {
    const groups = Array.from(card.querySelectorAll("[data-people-canvas-assignment-group]"))
    let run = []

    const flush = () => {
      if (run.length === 0) return
      this.insertPublicOverlapCollapseSummary(run, "publicOverlapCollapsedGroup")
      run = []
    }

    groups.forEach((group) => {
      const row = group.querySelector("[data-people-canvas-assignment-row]")
      const visible = row?.dataset.filterVisible === "true" && !group.classList.contains("hidden")

      if (!visible) {
        return
      }

      if (this.rowPotentiallyOverlappingForSelectedIdentity(row, selectedIdentityKey, publicViewMode)) {
        run.push(group)
      } else {
        flush()
      }
    })

    flush()
  }

  insertPublicOverlapCollapseSummary(elements, collapsedDatasetKey) {
    const firstElement = elements[0]
    const template = this.hasPublicOverlapCollapseTemplateTarget ? this.publicOverlapCollapseTemplateTarget : null
    if (!firstElement || !template?.content?.firstElementChild) return

    const summary = template.content.firstElementChild.cloneNode(true)
    const count = elements.length
    const labelElement = summary.querySelector("[data-public-overlap-collapse-label]")
    const actionElement = summary.querySelector("[data-public-overlap-collapse-reveal]")
    const cluster = document.createElement("div")
    cluster.className = "public-overlap-cluster"
    cluster.dataset.publicOverlapCluster = "true"
    const labelForState = (collapsed) => {
      const singular = collapsed ?
        this.translationFor("public_overlap_hidden.one", "1 overlapping job hidden.") :
        this.translationFor("public_overlap_hidden.shown_one", "1 overlapping job shown.")
      const plural = collapsed ?
        this.translationFor("public_overlap_hidden.other", "%{count} overlapping jobs hidden.") :
        this.translationFor("public_overlap_hidden.shown_other", "%{count} overlapping jobs shown.")

      return (count === 1 ? singular : plural).replace("%{count}", count)
    }
    const setCollapsed = (collapsed) => {
      elements.forEach((element) => {
        element.classList.toggle("hidden", collapsed)
        if (collapsed) {
          element.dataset[collapsedDatasetKey] = "true"
        } else {
          delete element.dataset[collapsedDatasetKey]
        }
      })

      cluster.dataset.publicOverlapClusterExpanded = collapsed ? "false" : "true"
      if (labelElement) this.setTextIfChanged(labelElement, labelForState(collapsed))
      if (actionElement) {
        actionElement.textContent = collapsed ?
          this.translationFor("public_overlap_hidden.reveal", "Click to reveal") :
          this.translationFor("public_overlap_hidden.hide_again", "Hide again")
      }
    }

    summary.addEventListener("click", () => {
      setCollapsed(!elements[0].classList.contains("hidden"))
    })

    setCollapsed(true)

    elements.forEach((element) => {
      const placeholder = document.createComment("public-overlap-placeholder")
      element.parentNode.insertBefore(placeholder, element)
      element.__publicOverlapPlaceholder = placeholder
    })

    // Assemble the group off-document so WebKit processes one insertion instead
    // of reconnecting the live group for every child.
    const anchor = firstElement.__publicOverlapPlaceholder
    const parent = anchor.parentNode
    cluster.append(summary, ...elements)
    parent.insertBefore(cluster, anchor)
  }

  applyInlineButtonGroup(group) {
    const segments = Array.from(group.querySelectorAll("[data-inline-button-segment]"))
    const useCornerShape = group.dataset.inlineButtonGroupShape === "corner"

    segments.forEach((segment) => {
      segment.classList.remove("rounded-full", "rounded-l-full", "rounded-r-full", "rounded-md", "rounded-l-md", "rounded-r-md", "rounded-none", "rounded-tl-md", "rounded-tr-md", "rounded-br-xl", "-ml-px")
    })

    const visibleSegments = segments.filter((segment) => !segment.classList.contains("hidden"))

    visibleSegments.forEach((segment, index) => {
      const first = index === 0
      const last = index === visibleSegments.length - 1
      const singleClasses = useCornerShape ? ["rounded-tl-md", "rounded-br-xl"] : ["rounded-full"]
      const firstClasses = useCornerShape ? ["rounded-tl-md"] : ["rounded-l-full"]
      const lastClasses = useCornerShape ? ["rounded-tr-md", "rounded-br-xl"] : ["rounded-r-full"]

      if (visibleSegments.length === 1) {
        segment.classList.add(...singleClasses)
      } else if (first) {
        segment.classList.add(...firstClasses)
      } else if (last) {
        segment.classList.add(...lastClasses, "-ml-px")
      } else {
        segment.classList.add("rounded-none", "-ml-px")
      }
    })
  }

  renderPublicViewModeControls(selectedMode, hasSelection = Boolean(this.selectedPublicIdentityKey())) {
    if (this.hasPublicViewModeButtonTarget) {
      this.publicViewModeButtonTargets.forEach((button) => {
        const available = hasSelection || this.publicViewModeAvailableWithoutIdentity(button.dataset.publicViewMode)
        const active = button.dataset.publicViewMode === selectedMode
        button.dataset.selected = active ? "true" : "false"
        button.setAttribute("aria-pressed", active ? "true" : "false")
        button.classList.toggle("hidden", !available)
      })

      const selectedButton = this.publicViewModeButtonTargets.find((button) =>
        button.dataset.selected === "true" && !button.classList.contains("hidden")
      )
      this.revealSelectedPublicViewMode(selectedButton)
    }
  }

  revealSelectedPublicViewMode(button) {
    if (!this.hasPublicViewModeGroupTarget || !button) return

    window.requestAnimationFrame(() => {
      if (!button.isConnected || !this.hasPublicViewModeGroupTarget) return

      const group = this.publicViewModeGroupTarget
      const groupRect = group.getBoundingClientRect()
      const buttonRect = button.getBoundingClientRect()

      if (buttonRect.left < groupRect.left) {
        group.scrollLeft -= groupRect.left - buttonRect.left
      } else if (buttonRect.right > groupRect.right) {
        group.scrollLeft += buttonRect.right - groupRect.right
      }
    })
  }

  setPublicViewMode(mode) {
    const selectedIdentityKey = this.selectedPublicIdentityKey()
    const fallbackMode = selectedIdentityKey ? (this.publicSelfSignupEnabledValue ? "mine_and_open" : "only_mine") : "all"
    let resolvedMode = this.validPublicViewMode(mode) ? mode : fallbackMode

    if (!selectedIdentityKey && !this.publicViewModeAvailableWithoutIdentity(resolvedMode)) {
      resolvedMode = "all"
    }

    this.currentPublicViewMode = resolvedMode
    this.renderPublicViewModeControls(resolvedMode, Boolean(selectedIdentityKey))
  }

  validPublicViewMode(mode) {
    const availableModes = this.publicSelfSignupEnabledValue ?
      ["only_mine", "only_open", "mine_and_open", "all"] :
      ["only_mine", "all"]

    return availableModes.includes(mode)
  }

  publicViewModeAvailableWithoutIdentity(mode) {
    return this.publicSelfSignupEnabledValue ? ["all", "only_open"].includes(mode) : mode === "all"
  }

  publicOverlapCollapsingEnabled(selectedIdentityKey = this.selectedPublicIdentityKey(), publicViewMode = this.selectedPublicViewMode(selectedIdentityKey)) {
    return this.publicSelfSignupEnabledValue &&
      Boolean(selectedIdentityKey) &&
      ["mine_and_open", "only_open"].includes(publicViewMode)
  }

  setTextIfChanged(element, value) {
    const text = value == null ? "" : String(value)
    // Equal text still replaces text nodes and triggers costly work in WebKit.
    if (element.textContent !== text) element.textContent = text
  }

  swapButtonClasses(element, defaultClasses, warningClasses, useWarningClasses) {
    if (defaultClasses === warningClasses) {
      this.replaceClassList(element, defaultClasses, true)
      return
    }

    const defaultClassNames = this.classNameList(defaultClasses)
    const warningClassNames = this.classNameList(warningClasses)
    const defaultClassSet = new Set(defaultClassNames)
    const warningClassSet = new Set(warningClassNames)
    const sharedClassNames = defaultClassNames.filter((className) => warningClassSet.has(className))
    const defaultOnlyClassNames = defaultClassNames.filter((className) => !warningClassSet.has(className))
    const warningOnlyClassNames = warningClassNames.filter((className) => !defaultClassSet.has(className))

    this.replaceClassNames(element, sharedClassNames, true)
    this.replaceClassNames(element, defaultOnlyClassNames, !useWarningClasses)
    this.replaceClassNames(element, warningOnlyClassNames, useWarningClasses)
  }

  replaceClassList(element, classNames, shouldAdd) {
    if (!classNames) return

    this.replaceClassNames(element, this.classNameList(classNames), shouldAdd)
  }

  replaceClassNames(element, classNames, shouldAdd) {
    classNames.forEach((className) => {
      element.classList.toggle(className, shouldAdd)
    })
  }

  classNameList(classNames) {
    return (classNames || "").split(/\s+/).filter(Boolean)
  }

  renderRootSections({showSlots}) {
    const slotsSection = this.element.querySelector("[data-people-canvas-root-section='slots']")

    if (slotsSection) {
      slotsSection.classList.toggle("hidden", !showSlots)
    }
  }

  populateConfigForm(event) {
    this.syncConfigInputs()
  }

  currentConfig() {
    return {
      plan_states: Object.fromEntries(this.planStates()),
      public_join_policies: Object.fromEntries(this.publicJoinPolicies()),
      slot_timeline_keys: Array.from(this.selectedSlotTimelineKeys()),
      show_past_assignments: this.hasPastToggleTarget && this.pastToggleTarget.checked,
      exclude_archived_plans: this.excludeArchivedPlansEnabled(),
      include_untimed_slots: !this.hasUntimedSlotsToggleTarget || this.untimedSlotsToggleTarget.checked,
      allow_public_self_signup: this.hasAllowPublicSelfSignupToggleTarget && this.allowPublicSelfSignupToggleTarget.checked,
      view_mode: this.currentViewModeValue || "list",
      timeline_sort: this.hasTimelineSortTarget ? this.timelineSortTarget.value : "hours",
      timeline_sort_direction: this.hasTimelineSortDirectionTarget ? this.timelineSortDirectionTarget.value : "desc",
      timeline_show_all_people: this.timelineShowAllPeopleEnabled(),
      timeline_planning_filter: this.element.querySelector("[data-people-filter-bar]") ? null : (this.selectedTimelinePlanningFilter() || null),
      people_search: this.selectedPeopleSearch() || null,
      people_planning_filters: this.selectedPeoplePlanningFilters(),
      people_planning_effects: this.selectedPeoplePlanningEffects(),
      grouping_compaction_strategy: this.hasGroupingCompactionStrategyTarget ? (this.groupingCompactionStrategyTarget.value || null) : "auto",
    }
  }

  publicJoinPolicies() {
    return this.hasPlanJoinPolicyTarget ?
      this.planJoinPolicyTargets.map((select) => [select.dataset.planId, select.value || "on_space"]) :
      []
  }

  syncConfigInputs() {
    if (!this.hasConfigInputTarget) return

    const serializedConfig = JSON.stringify(this.currentConfig())
    this.configInputTargets.forEach((input) => {
      input.value = serializedConfig
    })
  }

  async copyShareUrl(event) {
    const button = event.currentTarget
    const url = button.dataset.shareUrl
    if (!url) return

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url)
    } else {
      const input = this.element.querySelector("[data-people-canvas-share-url-input]")
      if (input) {
        input.focus()
        input.select()
        document.execCommand("copy")
      }
    }

    if (this.hasCopiedLabelValue) {
      const label = button.querySelector("[data-public-calendar-copy-label]") || button
      const originalLabel = label.textContent
      label.textContent = this.copiedLabelValue
      window.setTimeout(() => {
        label.textContent = originalLabel || this.copyLabelValue
      }, 1500)
    }
  }

  currentAllPlansState() {
    const states = [...new Set(this.listedPlanToggleTargets().map((toggle) => this.planStateForControl(toggle)))]
    if (states.length === 0) return "default"

    return states.length === 1 ? states[0] : "mixed"
  }

  nextAllPlansState() {
    switch (this.currentAllPlansState()) {
      case "default":
        return "show"
      case "show":
        return "reference"
      case "reference":
        return "default"
      default:
        return "default"
    }
  }

  nextPlanState(state) {
    switch (state) {
      case "default":
        return "show"
      case "show":
        return "reference"
      case "reference":
        return "default"
      default:
        return "default"
    }
  }

  planToggleConfig(state) {
    const configs = this.hasPlanToggleConfigsValue ? this.planToggleConfigsValue : {}

    return configs[state] || configs.default || {
      ariaChecked: "false",
      buttonClassName: "",
      boxClassName: "",
      iconClassName: "",
      title: this.translationFor("plan_states.default", "No plan filter"),
    }
  }

  planStateForControl(control) {
    if (!control) return "default"
    if (control.tagName === "SELECT") return control.value || "default"
    if (control.tagName === "INPUT") return control.checked ? "show" : "default"
    return control.dataset.planState || "default"
  }

  setPlanState(control, state) {
    if (!control) return

    if (control.tagName === "SELECT") {
      control.value = state
      return
    }

    if (control.tagName === "INPUT") {
      control.checked = state !== "default" && state !== "hide"
      control.indeterminate = false
      return
    }

    control.dataset.planState = state
    this.renderPlanToggle(control)
  }

  translationFor(path, fallback) {
    if (!this.hasTranslationsValue) return fallback

    const value = path.split(".").reduce((memo, key) => memo?.[key], this.translationsValue)
    return value || fallback
  }
}
